import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AssistenteFinanceiro from '../components/AssistenteFinanceiro';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';
import FluxoCaixaDiarioChart from '../components/FluxoCaixaDiarioChart';
import logoComece from '../assets/logo-comece.svg';


const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseFloat(valor));
};

const CORES_PIZZA = ['#00205b', '#00b5cc', '#f1be48', '#9063cd', '#1fa971', '#e5484d', '#7d8aa3'];

// Roleta de um dígito: gira de baixo pra cima (spins voltas) e assenta no alvo.
const ReelDigito = ({ value, delay = 0, spins = 2, duration = 1.6 }) => {
  const [rolou, setRolou] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setRolou(true)));
    return () => cancelAnimationFrame(id);
  }, []);
  const totalCels = spins * 10 + 10;
  const alvoIndex = spins * 10 + value;
  const translate = rolou ? alvoIndex : 0;
  const cels = [];
  for (let i = 0; i < totalCels; i++) cels.push(i % 10);
  return (
    <span style={{ display: 'inline-flex', height: '1em', lineHeight: 1, overflow: 'hidden', alignItems: 'flex-start' }}>
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(-${translate}em)`,
          transition: `transform ${duration}s cubic-bezier(0.16, 1, 0.3, 1)`,
          transitionDelay: `${delay}ms`,
          willChange: 'transform',
        }}
      >
        {cels.map((d, i) => (
          <span key={i} style={{ height: '1em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{d}</span>
        ))}
      </span>
    </span>
  );
};

// Saldo com efeito odômetro (os 2 últimos dígitos giram mais devagar)
const SaldoAnimado = ({ total }) => {
  if (total == null) return <>—</>;
  const texto = formatarValor(total);
  const reduzir = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduzir) return <>{texto}</>;
  const totalDigitos = (texto.match(/\d/g) || []).length;
  let idxDigito = 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
      {texto.split('').map((ch, i) => {
        if (ch >= '0' && ch <= '9') {
          const d = idxDigito;
          idxDigito += 1;
          const posDeTras = totalDigitos - d;
          const ehCentavo = posDeTras <= 2;
          const delay = ehCentavo ? d * 85 + (posDeTras === 2 ? 500 : 1000) : d * 85;
          const duracao = ehCentavo ? (posDeTras === 2 ? 3.0 : 3.8) : 1.6;
          const voltas = ehCentavo ? 3 : 2;
          return <ReelDigito key={i} value={Number(ch)} delay={delay} duration={duracao} spins={voltas} />;
        }
        return (
          <span key={i} style={{ display: 'inline-flex', height: '1em', lineHeight: 1, alignItems: 'center', whiteSpace: 'pre' }}>{ch}</span>
        );
      })}
    </span>
  );
};

// Donut moderno (Composição/Diversificação) com hover isolado + legenda
const DonutDespesas = ({ dados, total, cores, rotulo }) => {
  const [ativa, setAtiva] = useState(null);
  if (!dados || dados.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
        <p>Sem dados para exibir</p>
      </div>
    );
  }
  return (
    <div className="d-flex align-items-center justify-content-center gap-4 flex-wrap" style={{ minHeight: 360 }}>
      <div className="fmp-donut" style={{ position: 'relative', width: 290, height: 290, flex: 'none' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dados}
              dataKey="valor"
              nameKey="nome"
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={116}
              paddingAngle={2}
              cornerRadius={6}
              stroke="none"
              onMouseEnter={(_, idx) => setAtiva(idx)}
              onMouseLeave={() => setAtiva(null)}
            >
              {dados.map((entry, index) => (
                <Cell key={`pz-${index}`} fill={cores[index % cores.length]} style={{ cursor: 'pointer' }} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', textAlign: 'center', pointerEvents: 'none', padding: '0 30px' }}>
          {ativa != null && dados[ativa] ? (
            <>
              <span className="text-truncate" style={{ fontSize: 11.5, color: '#475467', fontWeight: 600, maxWidth: '140px' }}>
                {dados[ativa].nome}
              </span>
              <span style={{ fontFamily: "'Unbounded', sans-serif", fontWeight: 700, fontSize: 16, color: cores[ativa % cores.length] }}>
                {formatarValor(dados[ativa].valor)}
              </span>
              <span style={{ fontSize: 12, color: '#7d8aa3', fontWeight: 600 }}>
                {(total > 0 ? (dados[ativa].valor / total) * 100 : 0).toFixed(1)}% do total
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 11, color: '#7d8aa3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>{rotulo || 'Total'}</span>
              <span style={{ fontFamily: "'Unbounded', sans-serif", fontWeight: 700, fontSize: 16, color: '#0e1726' }}>
                {formatarValor(total)}
              </span>
            </>
          )}
        </div>
      </div>
      <div style={{ width: 250, minWidth: 200 }}>
        {dados.map((entry, index) => {
          const pct = total > 0 ? (entry.valor / total) * 100 : 0;
          return (
            <div
              key={`lz-${index}`}
              className="d-flex align-items-center justify-content-between"
              style={{ fontSize: 13, padding: '4px 8px', borderRadius: 8, background: ativa === index ? '#f4f6fb' : 'transparent', transition: 'background .15s' }}
              onMouseEnter={() => setAtiva(index)}
              onMouseLeave={() => setAtiva(null)}
            >
              <span className="d-flex align-items-center gap-2" style={{ color: '#475467', minWidth: 0 }}>
                <i style={{ width: 11, height: 11, borderRadius: 3, background: cores[index % cores.length], flex: 'none', display: 'inline-block' }}></i>
                <span className="text-truncate" style={{ maxWidth: '150px' }}>{entry.nome}</span>
              </span>
              <b style={{ fontVariantNumeric: 'tabular-nums', color: '#0e1726' }}>{pct.toFixed(1)}%</b>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MESES3 = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Remove os códigos numéricos do início do nome da categoria
const limparCategoria = (cat) => {
  if (!cat) return '';
  const limpo = String(cat).replace(/^(\s*(\(x\))?\s*[\d.]+\s*[-.]?\s*)+/i, '').trim();
  return limpo || String(cat);
};

const rangeUltimosMeses = (n) => {
  const hoje = new Date();
  const fim = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - (n - 1), 1);
  const iniYM = `${ini.getFullYear()}-${String(ini.getMonth() + 1).padStart(2, '0')}`;
  return [iniYM, fim];
};

const descricaoImposto = (nome) => {
  const n = (nome || '').toLowerCase();
  if (/simples|\bdas\b/.test(n)) return 'Simples Nacional (DAS): guia única que reúne vários tributos das micro e pequenas empresas.';
  if (/icms/.test(n)) return 'ICMS: imposto estadual sobre circulação de mercadorias e alguns serviços.';
  if (/iss|issqn/.test(n)) return 'ISS: imposto municipal sobre a prestação de serviços.';
  if (/cofins/.test(n)) return 'COFINS: contribuição federal sobre o faturamento.';
  if (/pis|pasep/.test(n)) return 'PIS: contribuição federal sobre o faturamento.';
  if (/csll/.test(n)) return 'CSLL: contribuição social sobre o lucro líquido.';
  if (/irpj|irrf|imposto de renda|\bir\b/.test(n)) return 'Imposto de Renda: tributo federal sobre o lucro (IRPJ) ou retido na fonte (IRRF).';
  if (/inss|previd|\bcpp\b/.test(n)) return 'INSS/Previdência: contribuição previdenciária sobre a folha e o pró-labore.';
  if (/fgts/.test(n)) return 'FGTS: depósito mensal do empregador (8% da remuneração).';
  if (/taxa/.test(n)) return 'Taxa: cobrança pública por um serviço específico.';
  return 'Tributo/imposto recolhido pela empresa no período.';
};

// Gráfico de impostos empilhado por tipo (hover isolado na legenda)
const GraficoImpostos = ({ linhas, tipos, onSegmentClick }) => {
  const [leg, setLeg] = useState(null);
  const renderTip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const itens = payload.filter((p) => (p.value || 0) > 0);
    if (!itens.length) return null;
    const total = itens.reduce((s, p) => s + (p.value || 0), 0);
    return (
      <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 235, boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)', border: '1px solid rgba(146,162,196,0.18)' }}>
        <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label} · Impostos</div>
        {itens.map((p, i) => {
          const t = tipos.find((x) => x.codigo === p.dataKey);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color || p.fill || (t && t.cor) || '#888', display: 'inline-block', flex: 'none' }}></span>
                {t ? t.nome : p.dataKey}
              </span>
              <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.value)}</b>
            </div>
          );
        })}
        <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9bacca', fontSize: 12.5 }}>Total do mês</span>
          <b style={{ color: '#fff', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(total)}</b>
        </div>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={360}>
      <BarChart data={linhas} margin={{ top: 16, right: 16, left: 8, bottom: 4 }} barCategoryGap="28%">
        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
        <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
        <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
        <Tooltip content={renderTip} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
        <Legend
          onMouseEnter={(o) => setLeg(o && (o.dataKey || o.value))}
          onMouseLeave={() => setLeg(null)}
          formatter={(key) => {
            const t = tipos.find((x) => x.codigo === key);
            const nome = t ? t.nome : key;
            const apagado = leg && leg !== key;
            return (
              <span title={descricaoImposto(nome)} style={{ cursor: 'help', opacity: apagado ? 0.35 : 1, transition: 'opacity .15s', fontWeight: leg === key ? 700 : 400 }}>
                {nome}<i className="fas fa-info-circle ms-1" style={{ fontSize: 11, color: '#9bacca' }}></i>
              </span>
            );
          }}
        />
        {tipos.map((t, idx) => (
          <Bar key={t.codigo} dataKey={t.codigo} name={t.codigo} stackId="imp" fill={t.cor}
            fillOpacity={leg && leg !== t.codigo ? 0.2 : 1} maxBarSize={90}
            radius={idx === tipos.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
            style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
            onClick={(d) => onSegmentClick && onSegmentClick(d && d.payload ? d.payload : d)} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// Evolução acumulada dos impostos (área vermelha)
const EvolucaoImpostos = ({ linhas, tipos }) => {
  let acc = 0;
  const dados = (linhas || []).map((l) => {
    const mesTotal = (tipos || []).reduce((s, t) => s + (l[t.codigo] || 0), 0);
    acc += mesTotal;
    return { mes: l.mes, mesTotal, acumulado: acc };
  });
  const totalFinal = acc;
  const fmtEixo = (v) => {
    if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toFixed(1).replace('.', ',')}M`;
    if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
    return `R$ ${v}`;
  };
  const renderTip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload || {};
    return (
      <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 205, boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)', border: '1px solid rgba(146,162,196,0.18)' }}>
        <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e5484d', display: 'inline-block' }}></span>Pago no mês
          </span>
          <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.mesTotal || 0)}</b>
        </div>
        <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9bacca', fontSize: 12.5 }}>Acumulado</span>
          <b style={{ color: '#f1be48', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.acumulado || 0)}</b>
        </div>
      </div>
    );
  };
  return (
    <div style={{ background: 'linear-gradient(180deg,#fafbfe 0%,#fff 60%)', border: '1px solid #eef1f6', borderRadius: 14, padding: '16px 12px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 8px', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#7d8aa3' }}>Total acumulado no período</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#00205b', fontFamily: "'Unbounded', sans-serif", fontVariantNumeric: 'tabular-nums' }}>{formatarValor(totalFinal)}</span>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={dados} margin={{ top: 14, right: 18, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id="gradEvolImpCA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e5484d" stopOpacity={0.32} />
              <stop offset="90%" stopColor="#e5484d" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="#eef1f6" vertical={false} />
          <XAxis dataKey="mes" tick={{ fontSize: 11.5, fill: '#98a4b8' }} axisLine={false} tickLine={false} dy={6} />
          <YAxis tickFormatter={fmtEixo} tick={{ fontSize: 11, fill: '#98a4b8' }} axisLine={false} tickLine={false} width={54} />
          <Tooltip content={renderTip} cursor={{ stroke: '#c9d4e8', strokeWidth: 1.5, strokeDasharray: '5 5' }} />
          <Area type="monotone" dataKey="acumulado" stroke="#e5484d" strokeWidth={3} fill="url(#gradEvolImpCA)"
            dot={{ r: 3.5, fill: '#fff', stroke: '#e5484d', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#e5484d', stroke: '#fff', strokeWidth: 2.5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const DashboardContaAzul = () => {
  // PEGA O ID DO CLIENTE DA URL
  const { cliente_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // primitivo: useSearchParams devolve uma instância nova a cada render e não
  // pode entrar direto na lista de dependências do efeito
  const querDashboard = searchParams.get('dashboard') === '1';

  // Guarda o cliente_id "atual" para descartar respostas de requisições
  // disparadas para um cliente anterior (troca rápida de cliente antes da
  // resposta chegar não pode sobrescrever a tela com dados de outro cliente).
  const clienteIdRef = useRef(cliente_id);
  clienteIdRef.current = cliente_id;
  const ehClienteAtual = (idRequisitado) => String(clienteIdRef.current) === String(idRequisitado);

  // Estados - Dados do Cliente
  const [dadosCliente, setDadosCliente] = useState(null);
  const [contasCliente, setContasCliente] = useState([]);

  // Liga/desliga os insights de IA (salvo na coluna `ia` de clientes_bpo)
  const [insightsIA, setInsightsIA] = useState(true);
  const toggleInsightsIA = async () => {
    const nv = !insightsIA;
    setInsightsIA(nv);
    const token = localStorage.getItem('access');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/ia/`,
        { ia: nv ? 'Sim' : 'Não' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDadosCliente((prev) => (prev ? { ...prev, ia: nv ? 'Sim' : 'Não' } : prev));
    } catch (e) {
      setInsightsIA(!nv);
    }
  };
  
  // Estados - Dados Financeiros
  const [dadosCompletos, setDadosCompletos] = useState(null);
  const [dadosFluxoDiario, setDadosFluxoDiario] = useState(null);
  const [dadosInvestimentos, setDadosInvestimentos] = useState(null);
  const [dadosDRE, setDadosDRE] = useState(null);

  // Card de Impostos
  const [impostosChart, setImpostosChart] = useState({ linhas: [], tipos: [], total: 0, ok: false });
  const [impostosMeses, setImpostosMeses] = useState(6);
  const [impostosIni, setImpostosIni] = useState('');
  const [impostosFim, setImpostosFim] = useState('');
  const [carregandoImpostos, setCarregandoImpostos] = useState(false);
  const [impostosLanc, setImpostosLanc] = useState({ aberto: false, titulo: '', carregando: false, itens: [], total: 0 });
  // Card Faturado x Recebido
  const [fatRec, setFatRec] = useState({ linhas: [], totalFat: 0, totalRec: 0, totalAtraso: 0, ok: false });
  const [fatRecMeses, setFatRecMeses] = useState(6);
  const [carregandoFatRec, setCarregandoFatRec] = useState(false);

  // Cards Comparativos Anuais (ano atual x ano anterior)
  const FATCOMP_VAZIO = { meses: [], ok: false, anoAtual: null, anoAnterior: null, totalAtual: 0, totalAnterior: 0, variacaoPct: null, ytdAtual: 0, ytdAnterior: 0, ytdVariacaoPct: null, ytdCorte: '' };
  const [fatComp, setFatComp] = useState(FATCOMP_VAZIO);
  const [fatCompBase, setFatCompBase] = useState('competencia'); // 'competencia' | 'pagamento'
  const [fatCompTipo, setFatCompTipo] = useState('barras'); // 'barras' | 'linhas'
  const [carregandoFatComp, setCarregandoFatComp] = useState(false);
  const [despComp, setDespComp] = useState(FATCOMP_VAZIO);
  const [despCompBase, setDespCompBase] = useState('competencia');
  const [despCompTipo, setDespCompTipo] = useState('barras');
  const [carregandoDespComp, setCarregandoDespComp] = useState(false);

  // Estados - Loading
  const [carregando, setCarregando] = useState(false);
  const [carregandoFluxoDiario, setCarregandoFluxoDiario] = useState(false);
  const [carregandoInvestimentos, setCarregandoInvestimentos] = useState(false);
  const [carregandoDRE, setCarregandoDRE] = useState(false);
  
  // Estados - UI
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [atualizando, setAtualizando] = useState(false);
  const [menuJanela, setMenuJanela] = useState(false);
  // O menu é renderizado por portal no body: o cabeçalho (.fmp-dash-hero) tem
  // overflow:hidden e recortava o dropdown. Guardamos a posição do botão.
  const botaoAtualizarRef = useRef(null);
  const [posMenu, setPosMenu] = useState({ top: 0, left: 0 });
  // Histórico do próprio cliente (carga_execucoes): quanto cada janela costuma
  // levar e como terminou a última. Vazio enquanto não houver execução gravada.
  const [estimativa, setEstimativa] = useState(null);
  const [decorrido, setDecorrido] = useState(0);     // segundos desde o disparo
  const [resultadoCarga, setResultadoCarga] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [modalFluxoDiario, setModalFluxoDiario] = useState(false);
  const [diaDetalhes, setDiaDetalhes] = useState(null);
  const [modalInvestimento, setModalInvestimento] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [modalCategoria, setModalCategoria] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  
  // Estados - Filtros DRE
  const [dataInicioDRE, setDataInicioDRE] = useState('');
  const [dataFimDRE, setDataFimDRE] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState(''); // categoria selecionada (filtro dos gráficos do DRE)
  const [modalRelatorio, setModalRelatorio] = useState(false);
  const [relatorioDados, setRelatorioDados] = useState({ titulo: '', tipo: '', itens: [] });

  const isLoading = useRef(false);

  // EFFECT: Carregar dados do cliente ao montar (e sempre que o cliente_id mudar)
  useEffect(() => {
    // O cliente 19 (Gerencial) abre na Posição do Dia, não no dashboard. O botão
    // "Dashboard" de lá manda ?dashboard=1 para pedir o dashboard de propósito —
    // sem isso o redirecionamento rebatia o clique e a tela ficava inalcançável.
    if (String(cliente_id) === '19' && !querDashboard) {
      navigate(`/Posicao_dia/19`, { replace: true });
      return;
    }

    if (cliente_id) {
      // console.log(`Dashboard Conta Azul - Cliente ID: ${cliente_id}`);
      carregarDadosCliente();
    }

    return () => {
      // console.log('Componente desmontado');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente_id, querDashboard]);

  // ========================================
  // FUNÇÃO: Carregar dados do cliente
  // ========================================
  const carregarDadosCliente = async () => {
    const idRequisitado = cliente_id;
    setCarregando(true);
    const token = localStorage.getItem('access');

    try {
      // console.log(`Buscando dados do cliente Conta Azul ID: ${cliente_id}`);

      // API CONTA AZUL
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/cliente-conta-azul/${cliente_id}/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!ehClienteAtual(idRequisitado)) return; // usuário já trocou de cliente

      if (response.data.success) {
        setDadosCliente(response.data.cliente);
        setInsightsIA((response.data.cliente?.ia || 'Sim') !== 'Não');

        // console.log(' Dados do cliente Conta Azul:', response.data.cliente);

        buscarUltimaAtualizacao();
        buscarEstimativa();

        // Iniciar carregamento dos módulos financeiros
        setTimeout(() => {
          if (ehClienteAtual(idRequisitado)) carregarDadosFinanceiros();
        }, 500);
      }
    } catch (error) {
      if (!ehClienteAtual(idRequisitado)) return;

      console.error('Erro ao carregar cliente:', error);

      if (error.response?.status === 404) {
        setMensagem('Cliente não encontrado');
        setTimeout(() => navigate('/Clientes_bpo'), 3000);
      } else if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro ao carregar dados do cliente');
      }
    } finally {
      if (ehClienteAtual(idRequisitado)) setCarregando(false);
    }
  };

  // ========================================
  // FUNÇÃO: Buscar última atualização ContaAzul
  // ========================================
  // "4m 30s" / "45s"
  const fmtDuracao = (seg) => {
    const n = Math.max(0, Math.round(Number(seg) || 0));
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60);
    const r = n % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
  };

  // Tempo típico da janela escolhida; cai para a média geral quando ainda não
  // há histórico daquela opção específica.
  const estimarSegundos = (dias) => {
    const e = estimativa?.estimativas;
    if (!e) return null;
    return e[String(dias)] ?? e.geral ?? null;
  };

  const buscarEstimativa = async () => {
    const idRequisitado = cliente_id;
    const token = localStorage.getItem('access');
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/estimativa-atualizacao-contaazul/${cliente_id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!ehClienteAtual(idRequisitado)) return;
      if (r.data?.success) setEstimativa(r.data);
    } catch (e) {
      // estimativa é conforto: silenciosa
    }
  };

  const buscarUltimaAtualizacao = async () => {
    const idRequisitado = cliente_id;
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/ultima-atualizacao-contaazul/${cliente_id}/`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!ehClienteAtual(idRequisitado)) return;
      if (response.data.success) {
        setUltimaAtualizacao(response.data.ultima_atualizacao);
      }
    } catch (e) {
      // silencioso
    }
  };

  // ========================================
  // FUNÇÃO: Atualizar sincronização ContaAzul
  // ========================================
  // menu de janelas aberto pelo botão Atualizar
  // Janelas oferecidas no menu do botão. Precisa bater com JANELAS_ATUALIZACAO
  // (views_contaazul.py) e JANELA_PERMITIDA (main.py do sincronizador): são N
  // dias para cada lado de HOJE, porque a carga apaga e regrava essa faixa.
  const JANELAS = [
    { dias: 15, rotulo: '15 dias', ajuda: 'Mais rápida — só o período recente' },
    { dias: 30, rotulo: '1 mês', ajuda: null },
    { dias: 60, rotulo: '2 meses', ajuda: 'Padrão do agendamento' },
    { dias: 90, rotulo: '3 meses', ajuda: 'Mais demorada' },
  ];

  // Intervalo real que será atualizado, para o usuário ver antes de escolher
  const faixaDaJanela = (dias) => {
    const ini = new Date(); ini.setDate(ini.getDate() - dias);
    const fim = new Date(); fim.setDate(fim.getDate() + dias);
    const f = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return `${f(ini)} a ${f(fim)}`;
  };

  const handleAtualizar = async (dias) => {
    if (!dadosCliente?.atualizacao_configurada) {
      setMensagem('URL de atualização não configurada para este cliente.');
      return;
    }
    setMenuJanela(false);
    const idRequisitado = cliente_id;
    const comecouEm = Date.now();
    setAtualizando(true);
    setDecorrido(0);
    setResultadoCarga({ janela: dias, estimado: estimarSegundos(dias) });
    setMensagem('');
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/disparar-atualizacao-contaazul/${cliente_id}/`,
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 1260000,
          params: dias ? { dias } : {} }
      );
      if (!ehClienteAtual(idRequisitado)) return;
      const levou = Math.round((Date.now() - comecouEm) / 1000);
      if (response.data.success) {
        setResultadoCarga({ janela: dias, levou, ok: true });
        setMensagem('');
      } else {
        setResultadoCarga({ janela: dias, levou, ok: false,
                            erro: response.data.mensagem || 'Erro na atualização.' });
        setMensagem('');
      }
    } catch (error) {
      if (!ehClienteAtual(idRequisitado)) return;
      setResultadoCarga({
        janela: dias,
        levou: Math.round((Date.now() - comecouEm) / 1000),
        ok: false,
        erro: 'Erro ao iniciar atualização. Tente novamente.',
      });
    } finally {
      if (ehClienteAtual(idRequisitado)) {
        setAtualizando(false);
        buscarUltimaAtualizacao();
        buscarEstimativa();
      }
    }
  };

  // Cronômetro do "está rodando há X" — 1s é suficiente e não pesa.
  useEffect(() => {
    if (!atualizando) return undefined;
    const t = setInterval(() => setDecorrido((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [atualizando]);

  // Menu por portal usa posição fixa: rolar ou redimensionar tiraria ele do
  // lugar, então fecha. Esc também fecha.
  useEffect(() => {
    if (!menuJanela) return undefined;
    const fechar = () => setMenuJanela(false);
    const porTecla = (e) => { if (e.key === 'Escape') setMenuJanela(false); };
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    window.addEventListener('keydown', porTecla);
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
      window.removeEventListener('keydown', porTecla);
    };
  }, [menuJanela]);

  // ========================================
  // FUNÇÃO: Carregar dados financeiros principais
  // ========================================
  const carregarDadosFinanceiros = async () => {
    if (isLoading.current) {
      // console.log('Já está carregando dados financeiros');
      return;
    }

    const idRequisitado = cliente_id;
    isLoading.current = true;
    setCarregando(true);

    const token = localStorage.getItem('access');

    try {
      // console.log('Iniciando carregamento dos dados financeiros Conta Azul...');

      //  API CONTA AZUL - EXTRATO BANCÁRIO
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/extrato-bancario-contaazul/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 900000
        }
      );

      if (!ehClienteAtual(idRequisitado)) return; // cliente trocado enquanto a requisição estava em andamento

      if (response.data.success) {
        setDadosCompletos(response.data.dados);
        setMensagem(' Dados carregados com sucesso!');

        // console.log(' Dados principais Conta Azul carregados, iniciando módulos...');

        // Carregar módulos sequencialmente
        setTimeout(async () => {
          if (!ehClienteAtual(idRequisitado)) return;
          try {
            await carregarFluxoDiario();
            await carregarInvestimentos();

            if (!ehClienteAtual(idRequisitado)) return;

            // Carregar DRE (mês atual: 1º ao último dia do mês corrente)
            const hoje = new Date();
            const dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
            const dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

            setDataInicioDRE(dataInicio);
            setDataFimDRE(dataFim);

            await carregarDRE(dataInicio, dataFim);

            // console.log(' Todos os módulos Conta Azul carregados com sucesso');
          } catch (error) {
            console.error('Erro ao carregar módulos:', error);
          }
        }, 1000);

        setTimeout(() => setMensagem(''), 5000);
      } else {
        setMensagem('Erro ao carregar dados das contas');
      }

    } catch (error) {
      if (!ehClienteAtual(idRequisitado)) return;

      console.error('Erro ao carregar dados:', error);

      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: Sistema demorou mais que 60 segundos.');
      } else if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro de conexão.');
      }
    } finally {
      if (ehClienteAtual(idRequisitado)) setCarregando(false);
      isLoading.current = false;
    }
  };

  // ========================================
  // FUNÇÃO: Carregar Fluxo de Caixa Diário
  // ========================================
  const carregarFluxoDiario = async () => {
    if (carregandoFluxoDiario) {
      // console.log('Fluxo Diário já está carregando');
      return;
    }

    const idRequisitado = cliente_id;
    setCarregandoFluxoDiario(true);

    const token = localStorage.getItem('access');

    try {
      // API CONTA AZUL
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/fluxo-caixa-diario-contaazul/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (!ehClienteAtual(idRequisitado)) return;

      if (response.data.success) {
        // console.log('Dados Fluxo Diário Conta Azul recebidos:', response.data.dados);
        setDadosFluxoDiario(response.data.dados);
      } else {
        console.error('Erro ao carregar Fluxo Diário:', response.data.erro);
      }

    } catch (error) {
      if (ehClienteAtual(idRequisitado)) console.error('Erro ao carregar Fluxo Diário:', error);
    } finally {
      // Sempre libera o "lock" de carregamento, mesmo se o cliente mudou nesse
      // meio tempo — senão a próxima troca de cliente fica travada em "carregando".
      setCarregandoFluxoDiario(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar Análise de Investimentos
  // ========================================
  const carregarInvestimentos = async () => {
    if (carregandoInvestimentos) {
      // console.log('Investimentos já está carregando');
      return;
    }

    const idRequisitado = cliente_id;
    setCarregandoInvestimentos(true);

    const token = localStorage.getItem('access');

    try {
      // API CONTA AZUL
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/analise-investimentos-contaazul/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (!ehClienteAtual(idRequisitado)) return;

      if (response.data.success) {
        // console.log('Dados Investimentos Conta Azul recebidos:', response.data.dados);
        setDadosInvestimentos(response.data.dados);
      } else {
        console.error('Erro ao carregar Investimentos:', response.data.erro);
      }

    } catch (error) {
      if (ehClienteAtual(idRequisitado)) console.error('Erro ao carregar Investimentos:', error);
    } finally {
      // Sempre libera o "lock" de carregamento, mesmo se o cliente mudou nesse
      // meio tempo — senão a próxima troca de cliente fica travada em "carregando".
      setCarregandoInvestimentos(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar DRE
  // ========================================
  const carregarDRE = async (inicio, fim) => {
    if (carregandoDRE) {
      // console.log('DRE já está carregando');
      return;
    }

    const idRequisitado = cliente_id;
    setCarregandoDRE(true);

    const token = localStorage.getItem('access');

    try {
      // API CONTA AZUL
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-contaazul/`,
        {
          params: {
            cliente_id: cliente_id,
            data_inicio: inicio,
            data_fim: fim
          },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (!ehClienteAtual(idRequisitado)) return;

      if (response.data.success) {
        // console.log('Dados DRE Conta Azul recebidos:', response.data.dados);
        setDadosDRE(response.data.dados);
      } else {
        console.error('Erro ao carregar DRE:', response.data.erro);
      }

    } catch (error) {
      if (ehClienteAtual(idRequisitado)) console.error('Erro ao carregar DRE:', error);
    } finally {
      // Sempre libera o "lock" de carregamento, mesmo se o cliente mudou nesse
      // meio tempo — senão a próxima troca de cliente fica travada em "carregando".
      setCarregandoDRE(false);
    }
  };

  // ========================================
  // FUNÇÕES: Filtros DRE
  // ========================================
  const aplicarFiltroDRE = () => {
    if (dataInicioDRE && dataFimDRE) {
      carregarDRE(dataInicioDRE, dataFimDRE);
    } else {
      setMensagem('Selecione ambas as datas');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  const limparFiltroDRE = () => {
    const hoje = new Date();
    const quinzeDiasAtras = new Date();
    quinzeDiasAtras.setDate(hoje.getDate() - 15);
    
    const dataInicio = quinzeDiasAtras.toISOString().split('T')[0];
    const dataFim = hoje.toISOString().split('T')[0];
    
    setDataInicioDRE(dataInicio);
    setDataFimDRE(dataFim);
    carregarDRE(dataInicio, dataFim);
  };

  // ========================================
  // FUNÇÕES: Modais
  // ========================================
  const mostrarDetalhesDia = (data) => {
    if (data && data.movimentos && data.movimentos.length > 0) {
      setDiaDetalhes(data);
      setModalFluxoDiario({ aberto: true, dados: data });
    }
  };

  const mostrarDetalhesInvestimento = (conta) => {
    setContaSelecionada(conta);
    setModalInvestimento({ aberto: true, dados: conta });
  };

  const abrirModalInvestimento = (conta) => {
    setModalInvestimento({ aberto: true, dados: conta });
  };

  const computarGraficoMensalConta = (movimentos) => {
    if (!movimentos || movimentos.length === 0) return [];
    const porMes = {};
    movimentos.forEach(mov => {
      const mes = mov.data_vencimento.substring(0, 7);
      if (!porMes[mes]) porMes[mes] = { mes, aportes: 0, resgates: 0, rendimentos: 0, saldo_acumulado: 0 };
      const valor = Math.abs(mov.total);
      if (mov.tipo_movimento === 'Aportes') porMes[mes].aportes += valor;
      else if (mov.tipo_movimento === 'Resgates') porMes[mes].resgates += valor;
      else if (mov.tipo_movimento === 'Rendimentos') porMes[mes].rendimentos += valor;
    });
    const meses = Object.values(porMes).sort((a, b) => a.mes.localeCompare(b.mes));
    let saldo = 0;
    meses.forEach(m => {
      saldo += m.aportes - m.resgates + m.rendimentos;
      m.saldo_acumulado = Math.round(saldo * 100) / 100;
    });
    return meses;
  };

  const fecharModalFluxoDiario = () => {
    setModalFluxoDiario(false);
  };

  const fecharModalInvestimento = () => {
    setModalInvestimento(false);
  };

  const abrirModalCategoriaDRE = (payload, tipo) => {
    setModalCategoria({ aberto: true, dados: payload, tipo });
  };

  const fecharModalCategoriaDRE = () => {
    setModalCategoria(false);
  };

  // Abre o relatório completo (todas as categorias, do maior ao menor valor)
  const abrirRelatorio = (tipo) => {
    const base = tipo === 'despesas' ? (dadosDRE?.despesas || []) : (dadosDRE?.receitas || []);
    const itens = base
      .filter((d) => !String(d.categoria || '').toLowerCase().includes('transfer'))
      .slice()
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    setRelatorioDados({
      titulo: tipo === 'despesas' ? 'Relatório completo — Despesas' : 'Relatório completo — Receitas',
      tipo,
      itens
    });
    setModalRelatorio(true);
  };

  // ========================================
  // FUNÇÕES: Auxiliares
  // ========================================
  const formatarMesAno = (data) => {
    if (!data) return 'Mês Atual';

    const texto = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(data));

    return texto.charAt(0).toUpperCase() + texto.slice(1);
  };

  const obterVariacaoReal = () => {
    if (!dadosCompletos?.analises?.variacao_percentual_real && dadosCompletos?.analises?.variacao_percentual_real !== 0) return 0;
    return dadosCompletos.analises.variacao_percentual_real;
  };

  // ========================================
  // DADOS PARA GRÁFICOS
  // ========================================
  const COLORS = ['#00205b', '#00b5cc', '#f1be48', '#d94f63', '#4a9c5d', '#8c6bc8', '#f0883b', '#6c757d', '#3d7ab8', '#a8842c'];

  const dadosGraficoBarras = dadosCompletos ? 
    dadosCompletos.extratos
      .filter(e => e.status === 'sucesso')
      .map(e => ({
        nome: e.conta_info.nome_curto || e.conta_info.nome,
        saldo: e.saldos.saldo_disponivel,
        tipo: e.conta_info.tipo,
        color: e.conta_info.cor
      })) : [];

  // Ordenado (ranking) para o gráfico horizontal de saldos
  const dadosGraficoBarrasOrdenado = [...dadosGraficoBarras].sort((a, b) => b.saldo - a.saldo);

  // Diversificação = distribuição do saldo POR TIPO DE CONTA (soma por tipo)
  const dadosDiversificacao = (() => {
    const map = {};
    dadosGraficoBarras.forEach((c) => {
      const t = (c.tipo || 'Outros').toString().trim() || 'Outros';
      if (!map[t]) map[t] = { nome: t, valor: 0 };
      map[t].valor += Math.abs(c.saldo || 0);
    });
    return Object.values(map).filter((d) => d.valor > 0).sort((a, b) => b.valor - a.valor);
  })();

  const renderBarraContaLabel = (props) => {
    const { x, y, width, height, index } = props;
    const item = dadosGraficoBarrasOrdenado[index];
    if (!item) return null;
    const nome = item.nome || '';
    const valor = formatarValor(item.saldo);
    const negativo = item.saldo < 0;
    const cy = y + height / 2;
    const len = Math.abs(width);
    const left = Math.min(x, x + width);
    const right = Math.max(x, x + width);
    const nomeW = nome.length * 7.5 + 16;
    const valorW = valor.length * 7 + 16;
    const cabeNome = len > nomeW + 8;
    const cabeAmbos = len > nomeW + valorW;
    if (!negativo) {
      return (
        <g style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          {cabeNome ? (
            <text x={left + 14} y={cy} dy={5} fill="#ffffff" fontSize={13} fontWeight={700}>{nome}</text>
          ) : (
            <text x={right + 8} y={cy} dy={5} fill="#475467" fontSize={13} fontWeight={600}>{nome}</text>
          )}
          {cabeAmbos ? (
            <text x={right - 14} y={cy} dy={5} textAnchor="end" fill="#ffffff" fontSize={12} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
          ) : (
            <text x={right + (cabeNome ? 10 : nomeW + 14)} y={cy} dy={5} fill="#0e1726" fontSize={12} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
          )}
        </g>
      );
    }
    return (
      <g style={{ fontFamily: "'Inter Tight', sans-serif" }}>
        {cabeNome ? (
          <text x={right - 14} y={cy} dy={5} textAnchor="end" fill="#ffffff" fontSize={13} fontWeight={700}>{nome}</text>
        ) : (
          <text x={left - 8} y={cy} dy={5} textAnchor="end" fill="#475467" fontSize={13} fontWeight={600}>{nome}</text>
        )}
        {cabeAmbos ? (
          <text x={left + 14} y={cy} dy={5} fill="#ffffff" fontSize={12} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        ) : (
          <text x={left - 8 - (cabeNome ? 0 : nomeW + 6)} y={cy} dy={5} textAnchor="end" fill="#e5484d" fontSize={12} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        )}
      </g>
    );
  };

  // Top 5 categorias do DRE (exclui transferências), ordenado desc.
  // Se houver categoria selecionada no filtro, mostra só ela (1 barra).
  const dreTop5 = (arr) => {
    const base = (arr || []).filter((d) => !String(d.categoria || '').toLowerCase().includes('transfer'));
    if (categoriaFiltro) {
      return base.filter((d) => d.categoria === categoriaFiltro);
    }
    return base.slice().sort((a, b) => Math.abs(b.total) - Math.abs(a.total)).slice(0, 5);
  };

  // Categoria selecionada no filtro (localizada nas despesas OU receitas do período)
  const catSelCA = (() => {
    if (!categoriaFiltro || !dadosDRE) return null;
    const d = (dadosDRE.despesas || []).find((c) => c.categoria === categoriaFiltro);
    if (d) return { ...d, _tipo: 'despesa' };
    const r = (dadosDRE.receitas || []).find((c) => c.categoria === categoriaFiltro);
    if (r) return { ...r, _tipo: 'receita' };
    return null;
  })();

  const totalTipoSelCA = catSelCA
    ? (catSelCA._tipo === 'despesa' ? (dadosDRE?.resumo_dre?.total_despesas || 0) : (dadosDRE?.resumo_dre?.total_receitas || 0))
    : (dadosDRE?.resumo_dre?.total_despesas || 0);

  // Donut: categoria selecionada x demais (do mesmo tipo). null = usa a composição padrão de despesas.
  const donutViewCA = catSelCA
    ? [
        { nome: limparCategoria(catSelCA.categoria), valor: Math.abs(catSelCA.total || 0) },
        { nome: 'Demais', valor: Math.max(totalTipoSelCA - Math.abs(catSelCA.total || 0), 0) },
      ]
    : null;

  // Evolução mensal filtrada por categoria (agrega os lançamentos da própria categoria por mês)
  const evolucaoMensalCA = (() => {
    if (!catSelCA) return dadosDRE?.evolucao_mensal || [];
    const porMes = {};
    (catSelCA.detalhes || []).forEach((mv) => {
      const mes = (mv.data_vencimento || '').slice(0, 7); // YYYY-MM
      if (!mes) return;
      porMes[mes] = (porMes[mes] || 0) + Math.abs(mv.total || 0);
    });
    const ehReceita = catSelCA._tipo === 'receita';
    return Object.keys(porMes).sort().map((mes) => ({
      mes,
      receitas: ehReceita ? porMes[mes] : 0,
      despesas: ehReceita ? 0 : porMes[mes],
      resultado: ehReceita ? porMes[mes] : -porMes[mes],
    }));
  })();

  // Rótulo das barras do DRE: nome DENTRO (branco) quando cabe, valor na ponta
  const criarRotuloDRE = (dados, corValor) => (props) => {
    const { x, y, width, height, index } = props;
    const item = dados[index];
    if (!item) return null;
    const nome = limparCategoria(item.categoria);
    const valor = formatarValor(Math.abs(item.total || 0));
    const cy = y + height / 2;
    const len = Math.abs(width);
    const right = Math.max(x, x + width);
    const nomeW = nome.length * 7 + 16;
    const valorW = valor.length * 7 + 12;
    const cabeNome = len > nomeW + 8;
    const cabeAmbos = len > nomeW + valorW;
    return (
      <g style={{ fontFamily: "'Inter Tight', sans-serif" }}>
        {cabeNome ? (
          <text x={x + 12} y={cy} dy={5} fill="#ffffff" fontSize={12} fontWeight={700}>{nome}</text>
        ) : (
          <text x={right + 8} y={cy} dy={5} fill="#475467" fontSize={12} fontWeight={600}>{nome}</text>
        )}
        {cabeAmbos ? (
          <text x={right - 12} y={cy} dy={5} textAnchor="end" fill="#ffffff" fontSize={11} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        ) : (
          <text x={right + (cabeNome ? 10 : nomeW + 12)} y={cy} dy={5} fill={corValor} fontSize={11} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        )}
      </g>
    );
  };

  const dadosGraficoPieCategoria = dadosCompletos && dadosCompletos.analises.resumo_por_categoria ?
    Object.entries(dadosCompletos.analises.resumo_por_categoria)
      .filter(([categoria, dados]) => dados.saldo_disponivel !== 0)
      .map(([categoria, dados]) => ({
        nome: dados.nome,
        valor: Math.abs(dados.saldo_disponivel),
        color: dados.cor,
        eh_negativo: dados.saldo_disponivel < 0
      }))
    : [];

  const obterDadosEvolucao = () => {
    const fluxo = dadosCompletos?.historico_real?.fluxo_caixa;
    if (!fluxo || fluxo.length === 0) return [];

    let acumulado = 0;
    return fluxo.map(item => {
      acumulado += (item.entradas || 0) - (item.saidas || 0);
      return {
        nome: item.nome,
        saldo: acumulado,
        eh_previsao: item.eh_previsao || false
      };
    });
  };

  // Sparkline do card de saldo consolidado (a partir da evolução real)
  const obterSparkConsol = () => {
    const vals = obterDadosEvolucao().filter(e => !e.eh_previsao).map(e => e.saldo);
    if (vals.length < 2) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const W = 320; const H = 54;
    const pts = vals.map((v, i) => {
      const x = (i / (vals.length - 1)) * W;
      const y = H - 6 - ((v - min) / range) * (H - 12);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const line = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p).join(' ');
    const area = `${line} L${W},${H} L0,${H} Z`;
    return { line, area };
  };

  // ===== Card de Impostos (Conta Azul: /api/impostos-mensais-cliente/) =====
  const carregarImpostos = async (iniYM, fimYM) => {
    if (!cliente_id || !iniYM || !fimYM) return;
    setCarregandoImpostos(true);
    const token = localStorage.getItem('access');
    let [a1, m1] = iniYM.split('-').map(Number);
    let [a2, m2] = fimYM.split('-').map(Number);
    if (a1 > a2 || (a1 === a2 && m1 > m2)) { [a1, m1, a2, m2] = [a2, m2, a1, m1]; }
    const segs = [];
    for (let y = a1; y <= a2; y++) segs.push({ ano: y, mi: (y === a1 ? m1 : 1), mf: (y === a2 ? m2 : 12) });
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/impostos-mensais-cliente/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf },
          headers: { 'Authorization': `Bearer ${token}` }, timeout: 90000,
        }).then((r) => ({ seg: s, data: r.data?.success ? r.data : null })).catch(() => ({ seg: s, data: null }))
      ));
      const tiposMap = new Map();
      const linhas = [];
      resps.forEach(({ seg, data }) => {
        (data?.tipos || []).forEach((t) => { if (!tiposMap.has(String(t.codigo))) tiposMap.set(String(t.codigo), t.nome); });
        const porMes = {};
        (data?.meses || []).forEach((x) => { porMes[x.mes] = x; });
        for (let m = seg.mi; m <= seg.mf; m++) {
          const x = porMes[m] || {};
          const row = { key: `${seg.ano}-${m}`, mes: `${MESES3[m - 1]}/${String(seg.ano).slice(2)}`, mesNum: m, ano: seg.ano };
          tiposMap.forEach((nome, cod) => { row[cod] = Math.abs(x[cod] || 0); });
          linhas.push(row);
        }
      });
      let tipos = Array.from(tiposMap.entries()).map(([codigo, nome], idx) => ({ codigo, nome, cor: CORES_PIZZA[idx % CORES_PIZZA.length] }));
      tipos = tipos.filter((t) => linhas.some((r) => (r[t.codigo] || 0) > 0));
      const total = linhas.reduce((s, r) => s + tipos.reduce((a, t) => a + (r[t.codigo] || 0), 0), 0);
      setImpostosChart({ linhas, tipos, total, ok: tipos.length > 0 });
    } catch (e) {
      setImpostosChart({ linhas: [], tipos: [], total: 0, ok: false });
    } finally {
      setCarregandoImpostos(false);
    }
  };

  // Clique numa barra de imposto -> lançamentos de TODOS os impostos daquele mês
  // (o mesmo total mostrado no tooltip do gráfico, não só a cor exata clicada)
  const abrirLancImpostos = async (linha) => {
    const titulo = `Impostos — ${linha.mes}`;
    setImpostosLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/impostos-lancamentos-cliente/`, {
        params: { cliente_id: cliente_id, ano: linha.ano, mes: linha.mesNum },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const itens = (r.data?.lancamentos || []).sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0 });
    }
  };

  // Clique em Recebido / Em atraso -> relatório de lançamentos do período (reusa o modal)
  const abrirLancFatRecPeriodo = async (tipo) => {
    const rotulo = tipo === 'recebido' ? 'Recebido' : 'Em atraso';
    setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    const porAno = {};
    (fatRec.linhas || []).forEach((l) => {
      if (!porAno[l.ano]) porAno[l.ano] = { ano: l.ano, mi: l.mesNum, mf: l.mesNum };
      porAno[l.ano].mi = Math.min(porAno[l.ano].mi, l.mesNum);
      porAno[l.ano].mf = Math.max(porAno[l.ano].mf, l.mesNum);
    });
    const segs = Object.values(porAno);
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/faturado-recebido-lancamentos/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf, tipo },
          headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000,
        }).then((r) => r.data?.lancamentos || []).catch(() => [])
      ));
      const itens = resps.flat().sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: false, itens: [], total: 0 });
    }
  };

  // ===== Card Faturado x Recebido (Conta Azul: /api/faturado-recebido/) =====
  const carregarFatRec = async (iniYM, fimYM) => {
    if (!cliente_id || !iniYM || !fimYM) return;
    setCarregandoFatRec(true);
    const token = localStorage.getItem('access');
    let [a1, m1] = iniYM.split('-').map(Number);
    let [a2, m2] = fimYM.split('-').map(Number);
    if (a1 > a2 || (a1 === a2 && m1 > m2)) { [a1, m1, a2, m2] = [a2, m2, a1, m1]; }
    const segs = [];
    for (let y = a1; y <= a2; y++) segs.push({ ano: y, mi: (y === a1 ? m1 : 1), mf: (y === a2 ? m2 : 12) });
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/faturado-recebido/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf },
          headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000,
        }).then((r) => ({ seg: s, data: r.data?.success ? r.data : null })).catch(() => ({ seg: s, data: null }))
      ));
      const linhas = [];
      resps.forEach(({ seg, data }) => {
        const porMes = {};
        (data?.meses || []).forEach((x) => { porMes[x.mes] = x; });
        for (let m = seg.mi; m <= seg.mf; m++) {
          const x = porMes[m] || {};
          linhas.push({ key: `${seg.ano}-${m}`, mes: `${MESES3[m - 1]}/${String(seg.ano).slice(2)}`, mesNum: m, ano: seg.ano, faturado: x.faturado || 0, recebido: x.recebido || 0, atrasado: x.atrasado || 0 });
        }
      });
      const totalFat = linhas.reduce((s, l) => s + l.faturado, 0);
      const totalRec = linhas.reduce((s, l) => s + l.recebido, 0);
      const totalAtraso = linhas.reduce((s, l) => s + l.atrasado, 0);
      setFatRec({ linhas, totalFat, totalRec, totalAtraso, ok: linhas.some((l) => l.faturado > 0 || l.recebido > 0) });
    } catch (e) {
      setFatRec({ linhas: [], totalFat: 0, totalRec: 0, totalAtraso: 0, ok: false });
    } finally {
      setCarregandoFatRec(false);
    }
  };

  useEffect(() => {
    if (!cliente_id) return;
    const [ini, fim] = rangeUltimosMeses(6);
    setImpostosMeses(6); setFatRecMeses(6);
    setImpostosIni(ini); setImpostosFim(fim);
    carregarImpostos(ini, fim);
    carregarFatRec(ini, fim);
    // eslint-disable-next-line
  }, [cliente_id]);

  // Faturamento comparativo (ano atual x ano anterior), por competência ou pagamento
  useEffect(() => {
    if (!cliente_id) return undefined;
    let ativo = true;
    const token = localStorage.getItem('access');
    setCarregandoFatComp(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo/`, {
      params: { cliente_id: cliente_id, base: fatCompBase },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 60000,
    }).then((r) => {
      if (!ativo) return;
      if (r.data?.success) {
        const meses = (r.data.meses || []).map((m) => ({ ...m, nome: MESES3[m.mes - 1] }));
        setFatComp({
          meses,
          ok: meses.some((m) => (m.atual || 0) > 0 || (m.anterior || 0) > 0),
          anoAtual: r.data.ano_atual,
          anoAnterior: r.data.ano_anterior,
          totalAtual: r.data.total_atual || 0,
          totalAnterior: r.data.total_anterior || 0,
          variacaoPct: r.data.variacao_pct,
          ytdAtual: r.data.ytd_atual || 0,
          ytdAnterior: r.data.ytd_anterior || 0,
          ytdVariacaoPct: r.data.ytd_variacao_pct,
          ytdCorte: r.data.ytd_corte || '',
        });
      } else {
        setFatComp(FATCOMP_VAZIO);
      }
    }).catch(() => {
      if (ativo) setFatComp(FATCOMP_VAZIO);
    }).finally(() => { if (ativo) setCarregandoFatComp(false); });
    return () => { ativo = false; };
  }, [cliente_id, fatCompBase]);

  // Despesas comparativas (ano atual x ano anterior), por competência ou pagamento
  useEffect(() => {
    if (!cliente_id) return undefined;
    let ativo = true;
    const token = localStorage.getItem('access');
    setCarregandoDespComp(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo/`, {
      params: { cliente_id: cliente_id, base: despCompBase, tipo: 'despesas' },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 60000,
    }).then((r) => {
      if (!ativo) return;
      if (r.data?.success) {
        const meses = (r.data.meses || []).map((m) => ({ ...m, nome: MESES3[m.mes - 1] }));
        setDespComp({
          meses,
          ok: meses.some((m) => (m.atual || 0) > 0 || (m.anterior || 0) > 0),
          anoAtual: r.data.ano_atual,
          anoAnterior: r.data.ano_anterior,
          totalAtual: r.data.total_atual || 0,
          totalAnterior: r.data.total_anterior || 0,
          variacaoPct: r.data.variacao_pct,
          ytdAtual: r.data.ytd_atual || 0,
          ytdAnterior: r.data.ytd_anterior || 0,
          ytdVariacaoPct: r.data.ytd_variacao_pct,
          ytdCorte: r.data.ytd_corte || '',
        });
      } else {
        setDespComp(FATCOMP_VAZIO);
      }
    }).catch(() => {
      if (ativo) setDespComp(FATCOMP_VAZIO);
    }).finally(() => { if (ativo) setCarregandoDespComp(false); });
    return () => { ativo = false; };
  }, [cliente_id, despCompBase]);

  // Clique nas barras dos comparativos anuais -> lançamentos daquele mês/ano (reusa o modal)
  const abrirLancComparativo = async (tipoCard, base, ano, linha) => {
    if (!ano || !linha) return;
    const rotulo = tipoCard === 'despesas' ? 'Despesas' : 'Faturamento';
    const titulo = `${rotulo} — ${linha.nome}/${String(ano).slice(2)} (${base === 'pagamento' ? 'pagamento' : 'competência'})`;
    setImpostosLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo-lancamentos/`, {
        params: { cliente_id: cliente_id, base, tipo: tipoCard, ano, mes: linha.mes },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const itens = (r.data?.lancamentos || []).sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0 });
    }
  };

  const obterDadosFluxoCaixa = () => {
    if (!dadosCompletos?.historico_real?.fluxo_caixa) return [];
    
    return dadosCompletos.historico_real.fluxo_caixa.map(item => ({
      nome: item.nome,
      entradas: item.entradas,
      saidas: item.saidas,
      saldo_liquido: item.saldo_liquido,
      eh_previsao: item.eh_previsao || false
    }));
  };

  const obterPrimeiraPrevisao = () => {
    const dados = obterDadosFluxoCaixa();
    const primeira = dados.find(d => d.eh_previsao);
    return primeira ? primeira.nome : null;
  };

  // ========================================
  // COMPONENTES DE TOOLTIP
  // ========================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const valor = payload[0].value;
      const corValor = valor >= 0 ? '#00C49F' : '#dc3545';
      
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '13px'
          }}>
            {label}
          </p>
          <p style={{
            color: '#ffffff',
            margin: 0,
            fontWeight: 500,
            fontSize: '17px'
          }}>
            Saldo: <span style={{ 
              color: corValor, 
              fontWeight: 600,
              marginLeft: '8px'
            }}>
              {formatarValor(valor)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomTooltipPie = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const valor = payload[0].value;
      const nome = payload[0].payload.nome;
      const ehNegativo = payload[0].payload.eh_negativo;
      const corValor = ehNegativo ? '#dc3545' : '#00C49F';
      
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '13px'
          }}>
            {nome}
          </p>
          <p style={{
            color: '#ffffff',
            margin: 0,
            fontWeight: 500,
            fontSize: '17px'
          }}>
            Valor: <span style={{ 
              color: corValor, 
              fontWeight: 600,
              marginLeft: '8px'
            }}>
              {ehNegativo ? '-' : ''}{formatarValor(valor)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload, fill } = props;
    
    if (payload.eh_previsao) {
      return (
        <g>
          <circle
            cx={cx}
            cy={cy}
            r={6}
            fill="white"
            stroke={fill}
            strokeWidth={2}
            strokeDasharray="3 3"
          />
          <circle
            cx={cx}
            cy={cy}
            r={3}
            fill={fill}
          />
        </g>
      );
    }
    
    return (
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill={fill}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  // ========================================
  // RENDERIZAÇÃO CONDICIONAL - LOADING
  // ========================================
  if (carregando && !dadosCliente) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">
              <i className="fas fa-rocket me-2"></i>
              Carregando dados do cliente Conta Azul...
            </p>
            <div className="progress mt-3" style={{height: '6px', maxWidth: '300px', margin: '0 auto'}}>
              <div className="progress-bar progress-bar-striped progress-bar-animated" style={{width: '100%'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDERIZAÇÃO CONDICIONAL - CLIENTE NÃO ENCONTRADO
  // ========================================
  if (!dadosCliente) {
    return (
      <div className="das-page">
        <div className="container-fluid mt-5">
          <div className="alert alert-danger text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
            <h4>Cliente não encontrado</h4>
            <p>O cliente ID {cliente_id} não foi encontrado ou você não tem permissão para acessá-lo.</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => navigate('/Clientes_bpo')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Voltar para Lista de Clientes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDERIZAÇÃO PRINCIPAL
  // ========================================
  return (
    <div className={`das-page fmp-dash-page ${insightsIA ? '' : 'ia-off'}`}>
      <div className="container-fluid mt-4">
        {/* ========================================
 {/* ========================================
    HEADER DINÂMICO
======================================== */}
<div className="row mb-4">
  <div className="col-12">
    <div className="card fmp-dash-hero">
      <div className="fmp-dash-grid" aria-hidden="true"></div>
      <div className="fmp-dash-aurora" aria-hidden="true">
        <span className="blob b1"></span>
        <span className="blob b2"></span>
        <span className="blob b3"></span>
      </div>
      <div className="card-body">
        <div className="fmp-dash-top">
          <div className="fmp-dash-brand">
            <img className="fmp-dash-brand-logo" src={logoComece} alt="Comece" />
            <span className="fmp-dash-brand-sep" aria-hidden="true"></span>
            <span className="fmp-dash-brand-tag">FMP</span>
          </div>
          <div className="fmp-dash-topright">
            <button
              type="button"
              className={`fmp-ia-toggle ${insightsIA ? 'on' : 'off'}`}
              onClick={toggleInsightsIA}
              title={insightsIA ? 'Insights de IA ativados — clique para desativar' : 'Insights de IA desativados — clique para ativar'}
              aria-pressed={insightsIA}
            >
              <i className="fas fa-brain"></i>
              <span className="lbl">IA</span>
              <span className="knob" aria-hidden="true"></span>
            </button>
            {dadosCliente.tipo_plano && (
              <span className={`fmp-dash-plan ${dadosCliente.tipo_plano === 'Premium' ? '' : 'demo'}`}>
                <i className={`fas ${dadosCliente.tipo_plano === 'Premium' ? 'fa-crown' : 'fa-flask'} me-1`}></i>
                {dadosCliente.tipo_plano === 'Premium' ? 'Versão Premium' : 'Versão Demo'}
              </span>
            )}
          </div>
        </div>

        <div className="fmp-dash-head">
          <div>
            <span className="fmp-dash-eyebrow">Dashboard Financeiro</span>
            <h1 className="fmp-dash-title">
              {dadosCliente.nome} <span className="grad">— a saúde do seu financeiro em uma tela</span>
            </h1>
            <div className="fmp-dash-metarow">
              <span><i className="fas fa-building"></i>CNPJ: {dadosCliente.cnpj}</span>
              {dadosCliente.regime_tributario && (
                <span><i className="fas fa-balance-scale"></i>Regime: {dadosCliente.regime_tributario}</span>
              )}
              <span><i className="fas fa-database"></i>Sistema: {dadosCliente.sistema?.toUpperCase() || 'N/A'}</span>
              {contasCliente && contasCliente.length > 0 && (
                <span><i className="fas fa-university"></i>{contasCliente.length} conta(s) monitorada(s)</span>
              )}
              {dadosCliente.nome_operador && (
                <span><i className="fas fa-user-tie"></i>Operador: {dadosCliente.nome_operador}</span>
              )}
            </div>
            <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="fmp-dash-mod"
                  ref={botaoAtualizarRef}
                  onClick={() => {
                    if (menuJanela) { setMenuJanela(false); return; }
                    const r = botaoAtualizarRef.current?.getBoundingClientRect();
                    if (r) {
                      const largura = 250;
                      setPosMenu({
                        top: r.bottom + 6,
                        left: Math.min(r.left, window.innerWidth - largura - 12),
                      });
                    }
                    setMenuJanela(true);
                  }}
                  disabled={atualizando || !dadosCliente?.atualizacao_configurada}
                  title={dadosCliente?.atualizacao_configurada ? 'Escolher o período a sincronizar' : 'URL de atualização não configurada'}
                  style={{ opacity: dadosCliente?.atualizacao_configurada ? 1 : 0.5 }}
                >
                  <i className={`fas fa-sync-alt${atualizando ? ' fa-spin' : ''}`}></i>
                  {atualizando ? 'Atualizando...' : 'Atualizar'}
                  {!atualizando && <i className="fas fa-chevron-down ms-2" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>}
                </button>

                {menuJanela && !atualizando && createPortal(
                  <>
                    {/* clique fora fecha */}
                    <div style={{ position: 'fixed', inset: 0, zIndex: 2140 }} onClick={() => setMenuJanela(false)}></div>
                    <div
                      role="menu"
                      style={{
                        position: 'fixed', top: posMenu.top, left: posMenu.left, zIndex: 2150,
                        width: 250, backgroundColor: '#fff', borderRadius: 10,
                        boxShadow: '0 8px 28px rgba(16,24,40,.18)', overflow: 'hidden',
                        border: '1px solid rgba(16,24,40,.08)',
                      }}
                    >
                      <div style={{
                        padding: '10px 14px 8px', fontSize: '0.7rem', fontWeight: 700,
                        letterSpacing: '.06em', textTransform: 'uppercase', color: '#667085',
                        borderBottom: '1px solid #f1f3f5',
                      }}>
                        Período a atualizar
                      </div>
                      {JANELAS.map((j) => (
                        <button
                          key={j.dias}
                          type="button"
                          role="menuitem"
                          onClick={() => handleAtualizar(j.dias)}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', border: 0,
                            background: 'transparent', padding: '10px 14px', cursor: 'pointer',
                          }}
                          onMouseEnter={(ev) => { ev.currentTarget.style.background = '#f7f9fc'; }}
                          onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <strong style={{ color: '#101828', fontSize: '0.9rem' }}>{j.rotulo}</strong>
                            <span style={{ color: '#667085', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {faixaDaJanela(j.dias)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 2 }}>
                            <span style={{ color: '#98a2b3', fontSize: '0.75rem' }}>{j.ajuda || ''}</span>
                            {estimarSegundos(j.dias) && (
                              <span style={{ color: '#0e7490', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                <i className="fas fa-clock me-1" style={{ fontSize: '0.68rem' }}></i>
                                ~{fmtDuracao(estimarSegundos(j.dias))}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                      <div style={{
                        padding: '8px 14px 10px', fontSize: '0.72rem', color: '#98a2b3',
                        borderTop: '1px solid #f1f3f5',
                      }}>
                        O período é contado para trás e para frente de hoje, porque títulos a vencer
                        também são regravados.
                      </div>
                      {/* 3 meses é o teto da tela; períodos maiores exigem carga integral */}
                      <div style={{
                        padding: '8px 14px 10px', fontSize: '0.72rem', color: '#7a4f00',
                        backgroundColor: '#fdf3d8', borderTop: '1px solid #f3e4bb',
                        display: 'flex', gap: 8, alignItems: 'flex-start',
                      }}>
                        <i className="fas fa-circle-info" style={{ marginTop: 2, flex: 'none' }}></i>
                        <span>
                          Para carregar contas <strong>acima de 3 meses</strong>, solicite ao time de
                          tecnologia.
                        </span>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
              {/* Fica sempre colado no botão: é a primeira coisa que se quer saber
                  ao chegar na tela, inclusive enquanto uma nova carga roda. */}
              {ultimaAtualizacao && (
                <span style={{ fontSize: '0.8rem', color: '#9bb0d4', whiteSpace: 'nowrap' }}>
                  <i className="fas fa-clock me-1"></i>
                  Última atualização: {ultimaAtualizacao}
                  {estimativa?.ultima?.duracao_seg > 0 && (
                    <> · levou {fmtDuracao(estimativa.ultima.duracao_seg)}</>
                  )}
                  {estimativa?.ultima?.registros > 0 && (
                    <> · {Number(estimativa.ultima.registros).toLocaleString('pt-BR')} registros</>
                  )}
                </span>
              )}

              {(atualizando || resultadoCarga?.levou !== undefined) && (
                <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,.18)' }}></span>
              )}

              {/* Rodando: tempo decorrido e barra contra o tempo típico */}
              {atualizando && (
                <div style={{ minWidth: 260, fontSize: '0.8rem', color: '#9bb0d4' }}>
                  <div className="d-flex justify-content-between gap-3">
                    <span>
                      <i className="fas fa-spinner fa-spin me-1"></i>
                      Atualizando {resultadoCarga?.janela ? faixaDaJanela(resultadoCarga.janela) : 'os últimos 2 meses'}
                    </span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDuracao(decorrido)}
                      {resultadoCarga?.estimado ? ` / ~${fmtDuracao(resultadoCarga.estimado)}` : ''}
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,.15)', marginTop: 6, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 999, background: '#00b5cc',
                      width: resultadoCarga?.estimado
                        ? `${Math.min(99, (decorrido / resultadoCarga.estimado) * 100)}%`
                        : '35%',
                      transition: 'width 1s linear',
                    }}></div>
                  </div>
                  {resultadoCarga?.estimado > 0 && decorrido > resultadoCarga.estimado && (
                    <div style={{ marginTop: 4, color: '#f1be48' }}>
                      Passou do tempo habitual. Pode ser um período maior ou lentidão na API.
                    </div>
                  )}
                </div>
              )}

              {/* Terminou: resultado com o tempo real */}
              {!atualizando && resultadoCarga?.levou !== undefined && (
                <span
                  style={{
                    fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: 8,
                    color: resultadoCarga.ok ? '#7CFC9A' : '#ff8a80',
                  }}
                >
                  <i className={`fas ${resultadoCarga.ok ? 'fa-circle-check' : 'fa-circle-xmark'}`}></i>
                  {resultadoCarga.ok
                    ? `Atualização concluída em ${fmtDuracao(resultadoCarga.levou)}`
                    : resultadoCarga.erro}
                  <button
                    type="button" onClick={() => setResultadoCarga(null)}
                    title="Dispensar"
                    style={{ border: 0, background: 'transparent', color: 'inherit', opacity: 0.7, cursor: 'pointer', padding: 0 }}
                  >
                    <i className="fas fa-xmark"></i>
                  </button>
                </span>
              )}
            </div>
          </div>

          <div className="fmp-dash-consol">
            <div className="ph">
              <span className="t">Saldo consolidado · todas as contas</span>
              {dadosCompletos && (
                <span className={`pill ${obterVariacaoReal() >= 0 ? 'green' : 'red'}`}>
                  {obterVariacaoReal() >= 0 ? '▲ +' : '▼ '}{obterVariacaoReal().toFixed(1).replace('.', ',')}% no mês
                </span>
              )}
            </div>
            <div className="big">
              {dadosCompletos ? <SaldoAnimado total={dadosCompletos.analises.total_saldo_disponivel} /> : '—'}
            </div>
            <div className="sub">
              {dadosCompletos
                ? `${dadosCompletos.analises.contas_ativas} de ${dadosCompletos.analises.total_contas} contas ativas`
                : 'Calculando saldo consolidado…'}
            </div>
            {(() => {
              const sp = dadosCompletos ? obterSparkConsol() : null;
              return sp ? (
                <svg className="spark" viewBox="0 0 320 54" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="dashConsolGradCA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#3fd093" stopOpacity="0.42" />
                      <stop offset="1" stopColor="#3fd093" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={sp.area} fill="url(#dashConsolGradCA)" />
                  <path d={sp.line} fill="none" stroke="#3fd093" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null;
            })()}
          </div>
        </div>

        <div className="fmp-dash-mods">
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Config_contas_omie/' + cliente_id)} title="Configurações de Contas">
            <i className="fas fa-cogs"></i>Contas
          </button>
          {dadosCliente.mod_projetos !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Projetos_cliente/' + cliente_id)} title="Projetos">
            <i className="fas fa-project-diagram"></i>Projetos
          </button>
          )}
          {dadosCliente.mod_saving !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Saving_cliente/' + cliente_id)} title="Saving — Controle de Pagamentos">
            <i className="fas fa-piggy-bank"></i>Saving
          </button>
          )}
          {dadosCliente.mod_projecao_orcamentaria !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Planejamento_orcamentario/' + cliente_id)} title="Projeção Orçamentária">
            <i className="fas fa-chart-pie"></i>Projeção Orçamentária
          </button>
          )}
          {dadosCliente.mod_dre_vertical !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Dre_vertical/' + cliente_id)} title="DRE Vertical (Demonstração do Resultado)">
            <i className="fas fa-file-alt"></i>DRE Vertical
          </button>
          )}
          {dadosCliente.mod_dre_horizontal !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Dre_horizontal/' + cliente_id)} title="DRE Horizontal (meses nas colunas)">
            <i className="fas fa-table-cells"></i>DRE Horizontal
          </button>
          )}
          {dadosCliente.mod_contas_pagar !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Apagar_cliente/' + cliente_id)} title="Contas a Pagar">
            <i className="fas fa-file-invoice-dollar"></i>Contas a Pagar
          </button>
          )}
          {dadosCliente.mod_contas_receber !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Receber_cliente/' + cliente_id)} title="Contas a Receber">
            <i className="fas fa-hand-holding-usd"></i>Contas a Receber
          </button>
          )}
          {dadosCliente.mod_posicao !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Posicao_cliente/' + cliente_id)} title="Posição Financeira">
            <i className="fas fa-chart-pie"></i>Posição
          </button>
          )}
          {dadosCliente.mod_fechamento !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/Fechamento_cliente/' + cliente_id)} title="Fechamento Mensal">
            <i className="fas fa-calculator"></i>Fechamento
          </button>
          )}
          {dadosCliente.mod_envio_notas !== 'Não' && (
          <button type="button" className="fmp-dash-mod" onClick={() => navigate('/EnvioNotas_cliente/' + cliente_id)} title="Envio de Notas Fiscais">
            <i className="fas fa-file-invoice-dollar"></i>Envio de Notas
          </button>
          )}
        </div>
      </div>
    </div>
  </div>
</div>

        <div className="fmp-dash-body">
        {/* Mensagens agora são exibidas como toast (react-toastify) */}
        {/* LOADING INICIAL */}
        {/* spinner enquanto NÃO houver dados — sem depender do flag 'carregando',
            que fica false por ~500ms entre o cadastro e os dados financeiros
            (era esse vão que deixava o corpo em branco sem load) */}
        {!dadosCompletos && (
          <div className="row">
            <div className="col-12">
              <div className="text-center mt-5 mb-5">
                <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-3 text-muted">
                  <i className="fas fa-rocket me-2"></i>
                  Inicializando Dashboard Conta Azul... Aguarde até 60 segundos
                </p>
                <div className="progress mt-3" style={{height: '6px', maxWidth: '300px', margin: '0 auto'}}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{width: '100%'}}></div>
                </div>
              </div>
            </div>
          </div>
        )}
{/* CONTEÚDO PRINCIPAL - DADOS CARREGADOS */}
        {dadosCompletos && (
          <>
            {/* Estatísticas dos Movimentos (insight de IA) */}
            {insightsIA && dadosCompletos.estatisticas_movimentos && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-info"></i>
                        Estatísticas Reais - Últimos {dadosCompletos.estatisticas_movimentos.meses_analisados} Meses
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Movimentos</h6>
                          <h5 className="text-primary mb-0">
                            <i className="fas fa-exchange-alt me-1"></i>
                            {dadosCompletos.estatisticas_movimentos.total_movimentos}
                          </h5>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Total Entradas</h6>
                          <h6 className="text-success mb-0">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.total_entradas)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Total Saídas</h6>
                          <h6 className="text-danger mb-0">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.total_saidas)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Saldo Líquido</h6>
                          <h6 className={`mb-0 ${dadosCompletos.estatisticas_movimentos.saldo_liquido_periodo >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(dadosCompletos.estatisticas_movimentos.saldo_liquido_periodo)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Média Entradas</h6>
                          <small className="text-info">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.media_entradas_mensal)}
                          </small>
                        </div>
                        <div className="col-md-2 text-center">
                          <h6 className="text-muted small mb-1">Média Saídas</h6>
                          <small className="text-warning">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.media_saidas_mensal)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Análise de Tendências */}
{/* Análise de Tendências (IA) */}
{insightsIA && dadosCompletos?.analises?.tendencias_ia &&
 Object.keys(dadosCompletos.analises.tendencias_ia).length > 0 && (
  <div className="row mb-4">
    <div className="col-12">
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <h5 className="card-title mb-0">
            <i className="fas fa-brain me-2"></i>
            Análise Preditiva - Comparação Histórico vs Previsão IA
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 text-center border-end">
              <div className="mb-2">
                <i className={`fas fa-arrow-${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
              </div>
              <h6 className="text-muted small">TENDÊNCIA - ENTRADAS</h6>
              <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                {dadosCompletos.analises.tendencias_ia.entradas?.tendencia_percentual >= 0 ? '+' : ''}
                {dadosCompletos.analises.tendencias_ia.entradas?.tendencia_percentual.toFixed(1)}%
              </h4>
              <small className="text-muted d-block">
                <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.entradas?.media_historica)}
              </small>
              <small className="text-primary">
                <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.entradas?.media_prevista)}
              </small>
            </div>
            
            <div className="col-md-4 text-center border-end">
              <div className="mb-2">
                <i className={`fas fa-arrow-${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}></i>
              </div>
              <h6 className="text-muted small">TENDÊNCIA - SAÍDAS</h6>
              <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}>
                {dadosCompletos.analises.tendencias_ia.saidas?.tendencia_percentual >= 0 ? '+' : ''}
                {dadosCompletos.analises.tendencias_ia.saidas?.tendencia_percentual.toFixed(1)}%
              </h4>
              <small className="text-muted d-block">
                <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saidas?.media_historica)}
              </small>
              <small className="text-warning">
                <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saidas?.media_prevista)}
              </small>
            </div>
            
            <div className="col-md-4 text-center">
              <div className="mb-2">
                <i className={`fas fa-balance-scale fa-2x ${dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
              </div>
              <h6 className="text-muted small">TENDÊNCIA - SALDO LÍQUIDO</h6>
              <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                {dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual >= 0 ? '+' : ''}
                {dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual.toFixed(1)}%
              </h4>
              <small className="text-muted d-block">
                <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saldo?.medio_historico)}
              </small>
              <small className="text-info">
                <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saldo?.medio_previsto)}
              </small>
            </div>
          </div>
          
          <hr />
          
         <div className="alert border-start border-4 mb-0 fmp-insight">
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div style={{ fontSize: '15px', lineHeight: 1.65, color: '#334c73' }}>
                              <strong style={{ color: '#00205b' }}><i className="fas fa-lightbulb text-warning me-2"></i>Insight IA:</strong>
                                         <p className="mb-2 mt-1">
  {dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' 
    ? `Tendência positiva identificada! Com base na análise dos dados históricos e padrões de comportamento financeiro, a inteligência artificial projeta um aumento aproximado de ${dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual.toFixed(1)}% no saldo líquido médio mensal. Esse cenário indica uma evolução saudável das suas finanças, possivelmente impulsionada por maior controle de despesas ou aumento de receitas. Recomenda-se manter as estratégias atuais e, se possível, reforçar investimentos ou criar reservas para potencializar ainda mais esse crescimento.`
    
    : `Atenção ao cenário financeiro! A análise preditiva da inteligência artificial aponta uma possível redução de ${Math.abs(dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual).toFixed(1)}% no saldo líquido médio mensal. Essa tendência pode estar relacionada ao aumento de despesas, queda de receitas ou desequilíbrios no fluxo financeiro. É altamente recomendável revisar gastos recorrentes, reavaliar despesas programadas e, se necessário, ajustar o planejamento financeiro para evitar impactos mais significativos no futuro.`}
</p>
                            </div>
                          </div>
                          <div className="col-md-4 text-end">
                            <span className="badge bg-primary me-1">
                              <i className="fas fa-tree me-1"></i>
                              Random Forest
                            </span>
                            <span className="badge bg-info">
                              <i className="fas fa-chart-line me-1"></i>
                              Linear Reg.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Mensagem quando não há dados futuros */}
            {dadosCompletos && (!dadosCompletos.analises?.tendencias_ia || 
              Object.keys(dadosCompletos.analises.tendencias_ia).length === 0) && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-4">
                      <i className="fas fa-info-circle fa-3x text-info mb-3"></i>
                      <h5 className="text-muted">Análise Preditiva Indisponível</h5>
                      <p className="text-muted mb-0">
                        Não há dados futuros suficientes no período para gerar previsões.
                        <br />
                        <small>A análise preditiva requer movimentos programados (contas a pagar/receber) nos próximos meses.</small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cards de Resumo */}
            <div className="row mb-4">
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-success me-2">
                        <i className="fas fa-coins fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Total Disponível</h6>
                      </div>
                    </div>
                    <h4 className="text-success mb-1">
                      {formatarValor(dadosCompletos.analises.total_saldo_disponivel)}
                    </h4>
                    <small className="text-muted">
                      {dadosCompletos.analises.contas_ativas} de {dadosCompletos.analises.total_contas} contas
                    </small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-primary me-2">
                        <i className="fas fa-chart-line fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Investimentos</h6>
                      </div>
                    </div>
                    <h4 className={`mb-1 ${(dadosCompletos.analises.total_investimentos || 0) >= 0 ? 'text-primary' : 'text-warning'}`}>
                      {formatarValor(Math.abs(dadosCompletos.analises.total_investimentos || 0))}
                    </h4>
                    <small className="text-muted">
                      {Math.abs(dadosCompletos.analises.percentual_investido || 0).toFixed(1)}% do patrimônio
                      {(dadosCompletos.analises.total_investimentos || 0) < 0 && (
                        <span className="text-warning ms-1">(saldo negativo)</span>
                      )}
                    </small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-info me-2">
                        <i className="fas fa-university fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Conta Corrente</h6>
                      </div>
                    </div>
                    <h4 className="text-info mb-1">
                      {formatarValor(dadosCompletos.analises.total_conta_corrente || 0)}
                    </h4>
                    <small className="text-muted">Liquidez imediata</small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className={`${obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'} me-2`}>
                        <i className="fas fa-percentage fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Variação Real</h6>
                      </div>
                    </div>
                    <h4 className={`${obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'} mb-1`}>
                      {obterVariacaoReal() >= 0 ? '+' : ''}{obterVariacaoReal().toFixed(1)}%
                    </h4>
                    <small className={obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'}>
                      {obterVariacaoReal() >= 0 ? 'Crescimento' : 'Redução'} mensal
                    </small>
                  </div>
                </div>
              </div>
            </div>

                   {/* Gráficos de Barras e Pizza */}
                      <div className="row mb-4">
                        <div className="col-lg-8">
                          <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white">
                              <h5 className="card-title mb-0">
                                <i className="fas fa-chart-bar me-2 text-primary"></i>
                                Saldos Atuais por Conta
                              </h5>
                            </div>
                            <div className="card-body">
                              <ResponsiveContainer width="100%" height={Math.max(260, dadosGraficoBarrasOrdenado.length * 60)}>
                                <BarChart
                                  data={dadosGraficoBarrasOrdenado}
                                  layout="vertical"
                                  margin={{ top: 8, right: 120, left: 130, bottom: 8 }}
                                  barCategoryGap="14%"
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                                  <XAxis type="number" tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                                  <YAxis type="category" dataKey="nome" hide />
                                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,32,91,0.04)' }} />
                                  <Bar dataKey="saldo" radius={[0, 10, 10, 0]} minPointSize={6} className="fmp-bar-shadow" label={renderBarraContaLabel} isAnimationActive={true}>
                                    {dadosGraficoBarrasOrdenado.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>
              
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-chart-pie me-2 text-success"></i>
                      Diversificação
                    </h5>
                  </div>
                  <div className="card-body">
                    {dadosDiversificacao.length > 0 ? (
                      <DonutDespesas
                        dados={dadosDiversificacao}
                        total={dadosDiversificacao.reduce((s, d) => s + (d.valor || 0), 0)}
                        cores={dadosDiversificacao.map((d, i) => d.color || CORES_PIZZA[i % CORES_PIZZA.length])}
                        rotulo="Saldo total"
                      />
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>Sem contas para exibir</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
       {/* Evolução do Saldo (Histórico + Previsão IA) */}
            {insightsIA && obterDadosEvolucao().length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-area me-2 text-success"></i>
                        Evolução do Saldo Acumulado (Histórico + Previsão IA)
                      </h5>
                      <div>
                        <span className="badge bg-success me-2">
                          <i className="fas fa-check me-1"></i>
                          Histórico
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-robot me-1"></i>
                          Previsão IA
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={350}>
                        <AreaChart data={obterDadosEvolucao()}>
                          <defs>
                            <linearGradient id="colorSaldoPos" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00C49F" stopOpacity={0.7}/>
                              <stop offset="95%" stopColor="#00C49F" stopOpacity={0.05}/>
                            </linearGradient>
                            <linearGradient id="colorSaldoNeg" x1="0" y1="1" x2="0" y2="0">
                              <stop offset="5%" stopColor="#dc3545" stopOpacity={0.7}/>
                              <stop offset="95%" stopColor="#dc3545" stopOpacity={0.05}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="nome" tick={{ fontSize: 12 }} />
                          <YAxis
                            tickFormatter={(value) => formatarValor(value)}
                            tick={{ fontSize: 12 }}
                            domain={([dataMin, dataMax]) => {
                              const abs = Math.max(Math.abs(dataMin), Math.abs(dataMax));
                              return [-abs * 1.15, abs * 1.15];
                            }}
                          />
                          <Tooltip
                            formatter={(value, name, props) => [
                              formatarValor(value),
                              props.payload.eh_previsao ? 'Previsto (IA)' : 'Saldo Real'
                            ]}
                          />

                          {obterPrimeiraPrevisao() && (
                            <ReferenceLine
                              x={obterPrimeiraPrevisao()}
                              stroke="#ff6b6b"
                              strokeDasharray="5 5"
                              strokeWidth={2}
                              label={{
                                value: '← Real | IA →',
                                position: 'top',
                                fill: '#ff6b6b',
                                fontSize: 12
                              }}
                            />
                          )}

                          <Area
                            type="monotone"
                            dataKey="saldo"
                            stroke={(obterDadosEvolucao().at(-1)?.saldo ?? 0) >= 0 ? '#00C49F' : '#dc3545'}
                            fill={(obterDadosEvolucao().at(-1)?.saldo ?? 0) >= 0 ? 'url(#colorSaldoPos)' : 'url(#colorSaldoNeg)'}
                            strokeWidth={3}
                            dot={<CustomDot fill="#00C49F" />}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                      <div className="mt-2 text-center">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Saldo consolidado de todas as contas (histórico + previsão IA)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fluxo de Caixa Mensal (IA) */}
            {insightsIA && obterDadosFluxoCaixa().length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-exchange-alt me-2 text-primary"></i>
                        Fluxo de Caixa - Histórico + Previsão IA
                      </h5>
                      <div>
                        <span className="badge bg-success me-2">
                          <i className="fas fa-database me-1"></i>
                          Dados Reais
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-brain me-1"></i>
                          IA
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={obterDadosFluxoCaixa()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="nome" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value, name, props) => {
                              const tipo = props.payload.eh_previsao ? ' (IA)' : ' (Real)';
                              return [formatarValor(value), name + tipo];
                            }}
                          />
                          <Legend />
                          
                          {obterPrimeiraPrevisao() && (
                            <ReferenceLine 
                              x={obterPrimeiraPrevisao()} 
                              stroke="#ff6b6b" 
                              strokeDasharray="5 5"
                              label={{ value: 'Previsão IA ↓', position: 'top', fill: '#ff6b6b' }}
                            />
                          )}
                          
                          <Line 
                            type="monotone" 
                            dataKey="entradas" 
                            name="Entradas" 
                            stroke="#28a745" 
                            strokeWidth={3}
                            dot={<CustomDot fill="#28a745" />}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="saidas" 
                            name="Saídas" 
                            stroke="#dc3545" 
                            strokeWidth={3}
                            dot={<CustomDot fill="#dc3545" />}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="saldo_liquido" 
                            name="Saldo Líquido" 
                            stroke="#007bff" 
                            strokeWidth={3}
                            strokeDasharray="8 8"
                            dot={<CustomDot fill="#007bff" />}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                FLUXO DE CAIXA DIÁRIO
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-calendar-day me-2"></i>
                        Fluxo de Caixa Diário - {formatarMesAno(dadosFluxoDiario?.resumo?.mes_referencia)} 
                      </h5>
                      <div>
                        <span className="badge bg-light text-dark me-2">
                          <i className="fas fa-check-circle me-1 text-success"></i>
                          Realizado
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-clock me-1"></i>
                          Previsto
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {carregandoFluxoDiario ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{color: '#005f6b'}}></div>
                        <p className="mt-3 text-muted">Carregando Fluxo de Caixa Diário...</p>
                      </div>
                    ) : dadosFluxoDiario ? (
                      <>
                        {/* Resumo do Mês */}
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Saldo Inicial</h6>
                            <h6 className="text-info mb-0">
                              <i className="fas fa-wallet me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.saldo_inicial)}
                            </h6>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Entradas Realizadas</h6>
                            <h6 className="text-success mb-0">
                              <i className="fas fa-arrow-down me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.total_entradas_realizadas)}
                            </h6>
                            <small className="text-muted">{dadosFluxoDiario.resumo.qtd_movimentos_realizados} mov.</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Saídas Realizadas</h6>
                            <h6 className="text-danger mb-0">
                              <i className="fas fa-arrow-up me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.total_saidas_realizadas)}
                            </h6>
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">Saldo Final Previsto</h6>
                            <h6 className={`mb-0 ${dadosFluxoDiario.resumo.saldo_final_previsto >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className="fas fa-flag-checkered me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.saldo_final_previsto)}
                            </h6>
                            <small className="text-muted">{dadosFluxoDiario.resumo.dias_restantes} dias restantes</small>
                          </div>
                        </div>
                        
                        {/* Gráfico de Barras - Entradas e Saídas por Dia */}
                        <h6 className="text-muted mb-3">
                          <i className="fas fa-chart-bar me-2"></i>
                          Movimentação Diária
                          <span 
                            className="badge ms-2"
                            style={{
                              backgroundColor: '#f32b2b',
                              color: 'white',
                              animation: 'pulse 2s infinite',
                              cursor: 'pointer'
                            }}
                          >
                            <i className="fas fa-hand-pointer me-1"></i>
                            Gráfico Interativo - Clique nas Barras
                          </span>
                        </h6>

                        <style>{`
                          @keyframes pulse {
                            0%, 100% { transform: scale(1); opacity: 1; }
                            50% { transform: scale(1.05); opacity: 0.9; }
                          }
                        `}</style>

                        <div style={{ height: 620 }}>
                          <FluxoCaixaDiarioChart
                            dados={(dadosFluxoDiario.dados_grafico || []).map(d => ({
                              ...d,
                              saidas: Math.abs(d.saidas || 0),
                            }))}
                            onClickDia={mostrarDetalhesDia}
                          />
                        </div>
                        
                        {/* Legenda explicativa */}
                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="alert alert-info border-start border-4 mb-0" style={{borderColor: '#005f6b'}}>
                              <div className="row align-items-center">
                                <div className="col-md-8">
                                  <small>
                                    <strong><i className="fas fa-info-circle me-1"></i>Como interpretar:</strong>
                                    <ul className="mb-0 mt-1">
                                      <li><span className="text-success">Barras verdes</span> = Entradas (recebimentos)</li>
                                      <li><span className="text-danger">Barras vermelhas</span> = Saídas (pagamentos)</li>
                                      <li><span className="text-primary">Linha azul</span> = Saldo acumulado (evolução do caixa)</li>
                                      <li><span className="text-danger">Linha vertical vermelha</span> = Dia atual</li>
                                      <li>Dias à direita da linha = <strong>Previsões</strong> (contas a pagar/receber)</li>
                                    </ul>
                                  </small>
                                </div>
                                <div className="col-md-4 text-end">
                                  <span className="badge bg-success me-1">
                                    <i className="fas fa-check me-1"></i>
                                    {dadosFluxoDiario.resumo.qtd_movimentos_realizados} Realizados
                                  </span>
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-clock me-1"></i>
                                    {dadosFluxoDiario.resumo.qtd_contas_pagar + dadosFluxoDiario.resumo.qtd_contas_receber} Previstos
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-calendar-day fa-3x mb-3" style={{color: '#005f6b'}}></i>
                        <p>Aguarde o carregamento automático do Fluxo de Caixa Diário</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* ========================================
                ANÁLISE INTELIGENTE DO FLUXO DIÁRIO
            ======================================== */}
            {insightsIA && dadosFluxoDiario?.analise_inteligente && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-brain me-2"></i>
                        Análise Inteligente - Fluxo de Caixa Diário
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Linha 1: Dias Destaque */}
                      <div className="row mb-4">
                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-arrow-up fa-2x text-success"></i>
                          </div>
                          <h6 className="text-muted small">Maior Entrada</h6>
                          <h5 className="text-success mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_maior_entrada.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.dia} - {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.data}
                          </small>
                          <small className="text-primary">
                            {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.qtd_movimentos} movimentos
                          </small>
                        </div>

                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-arrow-down fa-2x text-danger"></i>
                          </div>
                          <h6 className="text-muted small">Maior Saída</h6>
                          <h5 className="text-danger mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_maior_saida.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_maior_saida.dia} - {dadosFluxoDiario.analise_inteligente.dia_maior_saida.data}
                          </small>
                          <small className="text-warning">
                            {dadosFluxoDiario.analise_inteligente.dia_maior_saida.qtd_movimentos} movimentos
                          </small>
                        </div>

                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-thumbs-up fa-2x text-success"></i>
                          </div>
                          <h6 className="text-muted small">Melhor Saldo Diário</h6>
                          <h5 className="text-success mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.dia} - {dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.data}
                          </small>
                        </div>

                        <div className="col-md-3 text-center">
                          <div className="mb-2">
                            <i className={`fas fa-thumbs-down fa-2x ${dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor < 0 ? 'text-danger' : 'text-warning'}`}></i>
                          </div>
                          <h6 className="text-muted small">Pior Saldo Diário</h6>
                          <h5 className={`mb-1 ${dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor < 0 ? 'text-danger' : 'text-warning'}`}>
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_pior_saldo.dia} - {dadosFluxoDiario.analise_inteligente.dia_pior_saldo.data}
                          </small>
                        </div>
                      </div>

                      <hr />

                      {/* Linha 2: Concentração por Período */}
                      <h6 className="text-muted mb-3">
                        <i className="fas fa-chart-pie me-2"></i>
                        Concentração de Movimentos no Mês
                      </h6>
                      <div className="row mb-4">
                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <span className="badge bg-primary" style={{fontSize: '14px'}}>
                              Início do Mês (Dias 1-10)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saldo)}
                            </strong>
                          </div>
                        </div>

                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <span className="badge bg-info" style={{fontSize: '14px'}}>
                              Meio do Mês (Dias 11-20)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saldo)}
                            </strong>
                          </div>
                        </div>

                        <div className="col-md-4 text-center">
                          <div className="mb-2">
                            <span className="badge bg-warning text-dark" style={{fontSize: '14px'}}>
                              Fim do Mês (Dias 21+)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saldo)}
                            </strong>
                          </div>
                        </div>
                      </div>

                      <hr />

                <div className="row">
  <div className="col-md-8">
    <div className="alert border-start border-4 mb-0 fmp-insight">
      <h6 className="alert-heading">
        <i className="fas fa-lightbulb me-2 text-warning"></i>
        Insights Inteligentes
      </h6>
      <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#334c73' }}>
        <ul className="mb-0">
          <li>
            <strong>Média Diária:</strong> Ao longo do período analisado, suas entradas apresentam uma média de {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.entrada_media)} por dia, 
            enquanto as saídas atingem {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.saida_media)}. 
            Isso resulta em um saldo médio diário de {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.saldo_medio)}, 
            indicando o ritmo real de geração (ou consumo) de caixa no dia a dia.
          </li>

          <li>
            <strong>Taxa de Atividade:</strong> Foi registrada movimentação financeira em {dadosFluxoDiario.analise_inteligente.taxa_atividade}% dos dias do período 
            ({dadosFluxoDiario.analise_inteligente.total_dias_com_movimento} de {dadosFluxoDiario.resumo.dia_atual} dias). 
            Esse indicador demonstra o nível de consistência operacional e frequência de uso do caixa.
          </li>

          {dadosFluxoDiario.analise_inteligente.dias_criticos > 0 && (
            <li className="text-danger">
              <strong><i className="fas fa-exclamation-triangle text-warning me-1"></i>Dias Críticos:</strong> Foram identificados {dadosFluxoDiario.analise_inteligente.dias_criticos} dia(s) com saldo diário negativo. 
              Esse comportamento pode sinalizar descasamento entre entradas e saídas, sendo importante avaliar a distribuição dos pagamentos e recebimentos.
            </li>
          )}

          <li>
            <strong>Padrão de Concentração:</strong>{' '}
            {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas > 
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas &&
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas > 
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas
              ? 'A maior parte das entradas ocorre no início do mês, indicando possível concentração de receitas logo nos primeiros dias.'
              : dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas > 
                dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas
              ? 'As entradas se concentram no meio do mês, sugerindo um fluxo mais distribuído ao longo do período.'
              : 'O maior volume de entradas ocorre no final do mês, o que pode exigir maior controle de caixa ao longo dos dias anteriores.'
            }
          </li>
        </ul>
      </div>
    </div>
  </div>

                        <div className="col-md-4">
                          <div className="card bg-light border-0 h-100">
                            <div className="card-body text-center">
                              <h6 className="text-muted small mb-3">Resumo do Período</h6>
                              <div className="mb-2">
                                <small className="text-muted d-block">Dias Analisados</small>
                                <h4 className="text-primary mb-0">{dadosFluxoDiario.resumo.dia_atual}</h4>
                              </div>
                              <div className="mb-2">
                                <small className="text-muted d-block">Dias com Movimento</small>
                                <h5 className="text-info mb-0">{dadosFluxoDiario.analise_inteligente.total_dias_com_movimento}</h5>
                              </div>
                              {dadosFluxoDiario.analise_inteligente.dias_criticos > 0 && (
                                <div>
                                  <small className="text-danger d-block">Dias Críticos</small>
                                  <h5 className="text-danger mb-0">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    {dadosFluxoDiario.analise_inteligente.dias_criticos}
                                  </h5>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                COMPARATIVOS ANUAIS (FATURAMENTO E DESPESAS)
            ======================================== */}
            {[
              { chave: 'faturamento', dados: fatComp, base: fatCompBase, setBase: setFatCompBase, tipoGraf: fatCompTipo, setTipoGraf: setFatCompTipo, carregando: carregandoFatComp, titulo: 'Faturamento — Comparativo Anual', icone: 'fa-chart-column', rotuloYtd: 'Faturado até', rotuloPct: (d) => `% do faturamento de ${d.anoAnterior} já atingido`, pctBoaAcima: true, hintComp: 'Pela data de competência do título', hintPag: 'Pela data em que o valor foi recebido', infoComp: 'Base: competência — valores agrupados pela data de competência do título.', infoPag: 'Base: pagamento — valores agrupados pela data em que foram efetivamente recebidos.', vazio: 'Sem faturamento registrado nos dois anos para a base selecionada.' },
              { chave: 'despesas', dados: despComp, base: despCompBase, setBase: setDespCompBase, tipoGraf: despCompTipo, setTipoGraf: setDespCompTipo, carregando: carregandoDespComp, titulo: 'Despesas — Comparativo Anual', icone: 'fa-file-invoice-dollar', rotuloYtd: 'Gasto até', rotuloPct: (d) => `% das despesas de ${d.anoAnterior} já gasto`, pctBoaAcima: false, hintComp: 'Pela data de competência do título', hintPag: 'Pela data em que o valor foi efetivamente pago', infoComp: 'Base: competência — despesas agrupadas pela data de competência do título.', infoPag: 'Base: pagamento — despesas agrupadas pela data em que foram efetivamente pagas.', vazio: 'Sem despesas registradas nos dois anos para a base selecionada.' },
            ].map((cfg) => (
              <div className="row mb-4" key={`comp-${cfg.chave}`}>
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="card-title mb-0">
                          <i className={`fas ${cfg.icone} me-2`} style={{ color: '#00205b' }}></i>
                          {cfg.titulo} {cfg.dados.anoAnterior && cfg.dados.anoAtual ? `(${cfg.dados.anoAnterior} x ${cfg.dados.anoAtual})` : ''}
                        </h5>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <small className="text-muted me-1">Base:</small>
                          {[
                            { key: 'competencia', rotulo: 'Competência', hint: cfg.hintComp },
                            { key: 'pagamento', rotulo: 'Pagamento', hint: cfg.hintPag },
                          ].map((b) => (
                            <button
                              key={b.key}
                              type="button"
                              className="btn btn-sm"
                              title={b.hint}
                              style={{
                                borderRadius: '999px',
                                padding: '3px 14px',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                border: '1px solid ' + (cfg.base === b.key ? '#00205b' : '#d4e2fb'),
                                backgroundColor: cfg.base === b.key ? '#00205b' : '#fff',
                                color: cfg.base === b.key ? '#fff' : '#00205b',
                              }}
                              onClick={() => cfg.setBase(b.key)}
                            >
                              {b.rotulo}
                            </button>
                          ))}
                          <span className="mx-1" style={{ borderLeft: '1px solid #e2e8f4', height: 22 }}></span>
                          {[
                            { key: 'barras', icone: 'fa-chart-column', rotulo: 'Barras' },
                            { key: 'linhas', icone: 'fa-chart-line', rotulo: 'Linhas' },
                          ].map((t) => (
                            <button
                              key={t.key}
                              type="button"
                              className="btn btn-sm"
                              title={`Ver em ${t.rotulo.toLowerCase()}`}
                              style={{
                                borderRadius: '999px',
                                padding: '3px 14px',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                border: '1px solid ' + (cfg.tipoGraf === t.key ? '#00205b' : '#d4e2fb'),
                                backgroundColor: cfg.tipoGraf === t.key ? '#00205b' : '#fff',
                                color: cfg.tipoGraf === t.key ? '#fff' : '#00205b',
                              }}
                              onClick={() => cfg.setTipoGraf(t.key)}
                            >
                              <i className={`fas ${t.icone} me-1`}></i>{t.rotulo}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      {cfg.carregando ? (
                        <div className="text-center py-5">
                          <div className="spinner-border" style={{ color: '#00205b' }}></div>
                          <p className="mt-3 text-muted">Carregando comparativo...</p>
                        </div>
                      ) : cfg.dados.ok ? (
                        <>
                          <div className="row mb-3">
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Total {cfg.dados.anoAnterior}</h6>
                              <h4 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(cfg.dados.totalAnterior)}</h4>
                            </div>
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Total {cfg.dados.anoAtual}</h6>
                              <h4 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(cfg.dados.totalAtual)}</h4>
                            </div>
                            <div className="col-md-4 text-center">
                              <h6 className="text-muted small mb-1">{cfg.rotuloPct(cfg.dados)}</h6>
                              {(() => {
                                const pct = cfg.dados.totalAnterior > 0 ? (cfg.dados.totalAtual / cfg.dados.totalAnterior) * 100 : null;
                                const acima = pct !== null && pct >= 100;
                                const corAcima = cfg.pctBoaAcima ? 'text-success' : 'text-danger';
                                const corBarra = acima ? (cfg.pctBoaAcima ? '#1fa971' : '#e5484d') : '#00205b';
                                return (
                                  <>
                                    <h4 className={`mb-1 ${acima ? corAcima : ''}`} style={acima ? {} : { color: '#00205b' }}>
                                      {pct === null ? '—' : `${pct.toFixed(1).replace('.', ',')}%`}
                                    </h4>
                                    {pct !== null && (
                                      <div className="mx-auto" style={{ maxWidth: 180, height: 7, borderRadius: 999, backgroundColor: '#e8edf6', overflow: 'hidden' }}>
                                        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 999, backgroundColor: corBarra, transition: 'width .4s' }}></div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Acumulado até a mesma data (YTD) */}
                          <div className="rounded mb-4 py-3" style={{ backgroundColor: '#f6f8fc', border: '1px solid #e8edf6' }}>
                            <div className="row">
                              <div className="col-md-4 text-center border-end">
                                <h6 className="text-muted small mb-1">{cfg.rotuloYtd} {cfg.dados.ytdCorte}/{cfg.dados.anoAnterior}</h6>
                                <h5 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(cfg.dados.ytdAnterior)}</h5>
                              </div>
                              <div className="col-md-4 text-center border-end">
                                <h6 className="text-muted small mb-1">{cfg.rotuloYtd} {cfg.dados.ytdCorte}/{cfg.dados.anoAtual}</h6>
                                <h5 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(cfg.dados.ytdAtual)}</h5>
                              </div>
                              <div className="col-md-4 text-center">
                                <h6 className="text-muted small mb-1">Variação no período</h6>
                                <h5 className={`mb-0 ${(cfg.dados.ytdVariacaoPct ?? 0) >= 0 ? (cfg.pctBoaAcima ? 'text-success' : 'text-danger') : (cfg.pctBoaAcima ? 'text-danger' : 'text-success')}`}>
                                  {cfg.dados.ytdVariacaoPct === null || cfg.dados.ytdVariacaoPct === undefined
                                    ? '—'
                                    : `${cfg.dados.ytdVariacaoPct >= 0 ? '▲ +' : '▼ '}${String(cfg.dados.ytdVariacaoPct).replace('.', ',')}%`}
                                </h5>
                              </div>
                            </div>
                          </div>

                          {(() => {
                            const tooltipComp = ({ active, payload, label }) => {
                              if (!active || !payload || !payload.length) return null;
                              const row = payload[0].payload || {};
                              const ant = row.anterior || 0; const atu = row.atual || 0;
                              const varMes = ant > 0 ? ((atu - ant) / ant) * 100 : null;
                              const corVar = varMes === null ? '#9bacca'
                                : (varMes >= 0) === cfg.pctBoaAcima ? '#1fa971' : '#e5484d';
                              const linhaTip = (cor, nome, val) => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: cor, display: 'inline-block' }}></span>{nome}
                                  </span>
                                  <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(val)}</b>
                                </div>
                              );
                              return (
                                <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 230, border: '1px solid rgba(146,162,196,0.18)' }}>
                                  <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
                                  {linhaTip('#00b5cc', String(cfg.dados.anoAnterior || ''), ant)}
                                  {linhaTip('#6b9bd8', String(cfg.dados.anoAtual || ''), atu)}
                                  <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#9bacca', fontSize: 12.5 }}>Variação</span>
                                    <b style={{ color: corVar, fontSize: 13 }}>
                                      {varMes === null ? '—' : `${varMes >= 0 ? '+' : ''}${varMes.toFixed(1).replace('.', ',')}%`}
                                    </b>
                                  </div>
                                </div>
                              );
                            };
                            const eixos = (
                              <>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                                <XAxis dataKey="nome" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                                <YAxis domain={[0, 'auto']} tickFormatter={(v) => formatarValor(v)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                                <Legend
                                  iconSize={14}
                                  wrapperStyle={{ paddingTop: 10 }}
                                  formatter={(v) => <span style={{ fontSize: 15, fontWeight: 600, color: '#475467', margin: '0 12px 0 4px' }}>{v}</span>}
                                />
                              </>
                            );
                            return (
                              <ResponsiveContainer width="100%" height={380}>
                                {cfg.tipoGraf === 'linhas' ? (
                                  <ComposedChart data={cfg.dados.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }}>
                                    <defs>
                                      <linearGradient id={`compGradAnterior-${cfg.chave}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#00b5cc" stopOpacity={0.28} />
                                        <stop offset="70%" stopColor="#00b5cc" stopOpacity={0.06} />
                                        <stop offset="100%" stopColor="#00b5cc" stopOpacity={0} />
                                      </linearGradient>
                                      <linearGradient id={`compGradAtual-${cfg.chave}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#00205b" stopOpacity={0.30} />
                                        <stop offset="70%" stopColor="#00205b" stopOpacity={0.07} />
                                        <stop offset="100%" stopColor="#00205b" stopOpacity={0} />
                                      </linearGradient>
                                    </defs>
                                    {eixos}
                                    <Tooltip content={tooltipComp} cursor={{ stroke: '#c6d2e8', strokeDasharray: '4 4' }} />
                                    <Area type="monotone" dataKey="anterior" name={String(cfg.dados.anoAnterior || 'Ano anterior')}
                                      stroke="#00b5cc" strokeWidth={3.5} fill={`url(#compGradAnterior-${cfg.chave})`}
                                      style={{ filter: 'drop-shadow(0 6px 10px rgba(0,181,204,0.35))' }}
                                      dot={{ r: 4.5, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2 }}
                                      activeDot={{ r: 7, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2.5 }} />
                                    <Area type="monotone" dataKey="atual" name={String(cfg.dados.anoAtual || 'Ano atual')}
                                      stroke="#00205b" strokeWidth={3.5} fill={`url(#compGradAtual-${cfg.chave})`}
                                      style={{ filter: 'drop-shadow(0 6px 10px rgba(0,32,91,0.35))' }}
                                      dot={{ r: 4.5, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }}
                                      activeDot={{ r: 7, fill: '#00205b', stroke: '#fff', strokeWidth: 2.5 }} />
                                  </ComposedChart>
                                ) : (
                                  <BarChart data={cfg.dados.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }} barGap={4} barCategoryGap="26%">
                                    {eixos}
                                    <Tooltip content={tooltipComp} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
                                    <Bar dataKey="anterior" name={String(cfg.dados.anoAnterior || 'Ano anterior')} fill="#00b5cc" radius={[6, 6, 0, 0]} maxBarSize={34}
                                      style={{ cursor: 'pointer' }}
                                      onClick={(d) => abrirLancComparativo(cfg.chave, cfg.base, cfg.dados.anoAnterior, d && d.payload ? d.payload : d)} />
                                    <Bar dataKey="atual" name={String(cfg.dados.anoAtual || 'Ano atual')} fill="#00205b" radius={[6, 6, 0, 0]} maxBarSize={34}
                                      style={{ cursor: 'pointer' }}
                                      onClick={(d) => abrirLancComparativo(cfg.chave, cfg.base, cfg.dados.anoAtual, d && d.payload ? d.payload : d)} />
                                  </BarChart>
                                )}
                              </ResponsiveContainer>
                            );
                          })()}
                          <div className="text-center mt-2">
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              {cfg.base === 'competencia' ? cfg.infoComp : cfg.infoPag}
                            </small>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <i className={`fas ${cfg.icone} fa-3x mb-3`} style={{ color: '#cbd5e1' }}></i>
                          <p>{cfg.vazio}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* ========================================
                DRE - DEMONSTRAÇÃO DO RESULTADO
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  {/* Card Header com Filtro Moderno */}
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0" style={{color: '#000'}}>
                        <i className="fas fa-calculator me-2"></i>
                        Receitas & Despesas Realizadas
                      </h5>
                      <div>
                        {dadosDRE && (
                          <div>
                            <span className="badge bg-light text-dark me-2">
                              <i className="fas fa-university me-1"></i>
                              Sistema: CONTA AZUL
                            </span>
                            <span className="badge bg-success">
                              <i className="fas fa-chart-line me-1"></i>
                              Margem: {dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* FILTRO MODERNO */}
                    <div 
                      className="p-3 rounded" 
                      style={{
                        backgroundColor: 'rgba(216, 214, 214, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="row g-3 align-items-end">
                        {/* Botões Rápidos */}
                        <div className="col-md-4">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-clock me-2"></i>
                            Períodos Rápidos
                          </label>
                          <div className="d-grid gap-2">
                            <div className="btn-group" role="group">
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-calendar-day me-1"></i>
                                Mês Atual
                              </button>
                              
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth(), 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-arrow-left me-1"></i>
                                Anterior
                              </button>
                              
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-calendar-alt me-1"></i>
                                3 Meses
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Data Início */}
                        <div className="col-md-3">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-calendar-alt me-2"></i>
                            Data Início
                          </label>
                          <input 
                            type="date" 
                            className="form-control shadow-sm"
                            style={{
                              backgroundColor: 'white',
                              border: '2px solid rgba(0, 0, 0, 0.2)',
                              fontWeight: '500',
                              color: 'black'
                            }}
                            value={dataInicioDRE}
                            onChange={(e) => setDataInicioDRE(e.target.value)}
                          />
                        </div>
                        
                        {/* Data Fim */}
                        <div className="col-md-3">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-calendar-check me-2"></i>
                            Data Fim
                          </label>
                          <input 
                            type="date" 
                            className="form-control shadow-sm"
                            style={{
                              backgroundColor: 'white',
                              border: '2px solid rgba(0, 0, 0, 0.2)',
                              fontWeight: '500',
                              color: 'black'
                            }}
                            value={dataFimDRE}
                            onChange={(e) => setDataFimDRE(e.target.value)}
                          />
                        </div>
                        
                        {/* Botões de Ação */}
                        <div className="col-md-2">
                          <div className="d-grid gap-2">
                            <button 
                              className="btn shadow-sm"
                              style={{
                                fontWeight: '600',
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                backgroundColor: 'white',
                                color: 'black'
                              }}
                              onClick={aplicarFiltroDRE}
                              disabled={!dataInicioDRE || !dataFimDRE}
                            >
                              <i className="fas fa-search me-2"></i>
                              Filtrar
                            </button>
                            
                            <button 
                              className="btn shadow-sm"
                              style={{
                                fontWeight: '500',
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                backgroundColor: 'white',
                                color: 'black'
                              }}
                              onClick={limparFiltroDRE}
                            >
                              <i className="fas fa-redo me-2"></i>
                              Resetar
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Filtro por categoria */}
                      <div className="row g-3 mt-1 align-items-end">
                        <div className="col-md-8">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-filter me-2"></i>
                            Categoria
                          </label>
                          <select
                            className="form-select shadow-sm"
                            style={{ backgroundColor: 'white', border: '2px solid rgba(0, 0, 0, 0.2)', fontWeight: '500', color: 'black' }}
                            value={categoriaFiltro}
                            onChange={(e) => setCategoriaFiltro(e.target.value)}
                          >
                            <option value="">Todas as categorias</option>
                            <optgroup label="Despesas">
                              {(dadosDRE?.despesas || [])
                                .filter((d) => !String(d.categoria || '').toLowerCase().includes('transfer'))
                                .slice().sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
                                .map((c) => (
                                  <option key={`fd-${c.categoria}`} value={c.categoria}>
                                    {limparCategoria(c.categoria)}
                                  </option>
                                ))}
                            </optgroup>
                            <optgroup label="Receitas">
                              {(dadosDRE?.receitas || [])
                                .slice().sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
                                .map((c) => (
                                  <option key={`fr-${c.categoria}`} value={c.categoria}>
                                    {limparCategoria(c.categoria)}
                                  </option>
                                ))}
                            </optgroup>
                          </select>
                        </div>
                        {categoriaFiltro && (
                          <div className="col-md-4">
                            <button
                              className="btn shadow-sm w-100"
                              style={{ fontWeight: '500', border: '2px solid rgba(0, 0, 0, 0.2)', backgroundColor: 'white', color: 'black' }}
                              onClick={() => setCategoriaFiltro('')}
                            >
                              <i className="fas fa-times me-2"></i>
                              Limpar categoria
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Badge Informativo */}
                      {dataInicioDRE && dataFimDRE && (
                        <div className="text-center mt-3">
                          <span 
                            className="badge" 
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              color: 'black',
                              fontSize: '0.85rem',
                              padding: '0.5rem 1rem',
                              border: '1px solid rgba(0, 0, 0, 0.2)'
                            }}
                          >
                            <i className="fas fa-info-circle me-2"></i>
                            Período: {new Date(dataInicioDRE + 'T00:00:00').toLocaleDateString('pt-BR')} 
                            {' até '}
                            {new Date(dataFimDRE + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    {carregandoDRE ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{color: '#ff6b35'}}></div>
                        <p className="mt-3 text-muted">Processando DRE Conta Azul...</p>
                      </div>
                    ) : dadosDRE ? (
                      <>
                        {/* Resumo DRE */}
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Total Receitas</h6>
                            <h5 className="text-success mb-0">
                              <i className="fas fa-plus-circle me-1"></i>
                              {formatarValor(dadosDRE.resumo_dre.total_receitas)}
                            </h5>
                            <small className="text-muted">{dadosDRE.resumo_dre.total_categorias_receita} categorias</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Total Despesas</h6>
                            <h5 className="text-danger mb-0">
                              <i className="fas fa-minus-circle me-1"></i>
                              {formatarValor(dadosDRE.resumo_dre.total_despesas)}
                            </h5>
                            <small className="text-muted">{dadosDRE.resumo_dre.total_categorias_despesa} categorias</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Resultado Líquido</h6>
                            <h5 className={`mb-0 ${dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className={`fas ${dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'} me-1`}></i>
                              {formatarValor(dadosDRE.resumo_dre.resultado_liquido)}
                            </h5>
                            <small className={dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'text-success' : 'text-danger'}>
                              {dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'Lucro' : 'Prejuízo'}
                            </small>
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">Margem Líquida</h6>
                            <h5 className={`mb-0 ${dadosDRE.resumo_dre.margem_liquida >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className="fas fa-percentage me-1"></i>
                              {dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%
                            </h5>
                            <small className="text-muted">Eficiência operacional</small>
                          </div>
                        </div>

                        {/* Continuar com gráficos DRE... */}
                        {/* Gráficos DRE - Despesas e Receitas */}
                        <div className="row mb-4">
                         {/* Gráfico Despesas */}
{/* Gráfico Despesas */}
<div className="col-lg-6">
  <div className="card border-0 bg-light h-100">
    <div className="card-header bg-white border-0">
      <h6 className="mb-0">
        <i className="fas fa-arrow-down text-danger me-2"></i>
        Top 5 Despesas
        <span
          className="badge ms-2"
          style={{
            backgroundColor: '#eaf2ff',
            color: '#00205b',
            fontSize: '11px'
          }}
        >
          <i className="fas fa-hand-pointer me-1"></i>
          Clique nas Barras
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary ms-2"
          style={{ fontSize: '0.72rem', padding: '2px 9px', borderRadius: '999px' }}
          onClick={() => abrirRelatorio('despesas')}
        >
          <i className="fas fa-list me-1"></i>Ver todo o relatório
        </button>
      </h6>
    </div>
    <div className="card-body">
      {dreTop5(dadosDRE.despesas).length > 0 ? (
        <ResponsiveContainer width="100%" height={Math.max(220, dreTop5(dadosDRE.despesas).length * 56)}>
          <BarChart data={dreTop5(dadosDRE.despesas)} layout="vertical" margin={{ top: 6, right: 96, left: 8, bottom: 6 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
            <XAxis type="number" tickFormatter={(value) => formatarValor(Math.abs(value))} tick={{ fontSize: 10, fill: '#7d8aa3' }} />
            <YAxis type="category" dataKey="categoria" hide />
            <Tooltip
              formatter={(value) => formatarValor(Math.abs(value))}
              labelFormatter={(label) => limparCategoria(label)}
              cursor={{ fill: 'rgba(229,72,77,0.06)' }}
            />
            <Bar
              dataKey="total"
              radius={[0, 10, 10, 0]}
              minPointSize={6}
              className="fmp-bar-shadow"
              label={criarRotuloDRE(dreTop5(dadosDRE.despesas), '#e5484d')}
              style={{ cursor: 'pointer' }}
              onClick={(data) => {
                const detalhes = data.detalhes || [];
                abrirModalCategoriaDRE({ categoria: data.categoria, total: data.total, quantidade: detalhes.length, movimentos: detalhes }, 'despesa');
              }}
            >
              {dreTop5(dadosDRE.despesas).map((entry, index) => (
                <Cell key={`cell-d-${index}`} fill={`hsl(2, 70%, ${44 + index * 4}%)`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
                                ) : (
                                  <div className="text-center py-5 text-muted">
                                    <i className="fas fa-inbox fa-2x mb-2"></i>
                                    <p className="mb-0">Sem despesas no período</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Gráfico Receitas */}
                          {/* Gráfico Receitas */}
<div className="col-lg-6">
  <div className="card border-0 bg-light h-100">
    <div className="card-header bg-white border-0">
      <h6 className="mb-0">
        <i className="fas fa-arrow-up text-success me-2"></i>
        Top 5 Receitas
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary ms-2"
          style={{ fontSize: '0.72rem', padding: '2px 9px', borderRadius: '999px' }}
          onClick={() => abrirRelatorio('receitas')}
        >
          <i className="fas fa-list me-1"></i>Ver todo o relatório
        </button>
      </h6>
    </div>
    <div className="card-body">
      {dreTop5(dadosDRE.receitas).length > 0 ? (
        <ResponsiveContainer width="100%" height={Math.max(220, dreTop5(dadosDRE.receitas).length * 56)}>
          <BarChart data={dreTop5(dadosDRE.receitas)} layout="vertical" margin={{ top: 6, right: 96, left: 8, bottom: 6 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
            <XAxis type="number" tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 10, fill: '#7d8aa3' }} />
            <YAxis type="category" dataKey="categoria" hide />
            <Tooltip
              formatter={(value) => formatarValor(value)}
              labelFormatter={(label) => limparCategoria(label)}
              cursor={{ fill: 'rgba(31,169,113,0.06)' }}
            />
            <Bar
              dataKey="total"
              radius={[0, 10, 10, 0]}
              minPointSize={6}
              className="fmp-bar-shadow"
              label={criarRotuloDRE(dreTop5(dadosDRE.receitas), '#1fa971')}
              style={{ cursor: 'pointer' }}
              onClick={(data) => {
                const detalhes = data.detalhes || [];
                abrirModalCategoriaDRE({ categoria: data.categoria, total: data.total, quantidade: detalhes.length, movimentos: detalhes }, 'receita');
              }}
            >
              {dreTop5(dadosDRE.receitas).map((entry, index) => (
                <Cell key={`cell-r-${index}`} fill={`hsl(145, 55%, ${38 + index * 4}%)`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
                                ) : (
                                  <div className="text-center py-5 text-muted">
                                    <i className="fas fa-inbox fa-2x mb-2"></i>
                                    <p className="mb-0">Sem receitas no período</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Evolução Mensal + Composição das Despesas */}
                        {dadosDRE.evolucao_mensal && dadosDRE.evolucao_mensal.length > 0 && (
                          <div className="row mb-4">
                            <div className="col-lg-6 mb-3">
                              <h6 className="text-muted mb-3">
                                <i className="fas fa-chart-line me-2"></i>
                                {catSelCA ? `${limparCategoria(catSelCA.categoria)} — evolução mensal` : 'Evolução Mensal - Receitas vs Despesas'}
                              </h6>
                              {evolucaoMensalCA.length > 0 ? (
                                <ResponsiveContainer width="100%" height={360}>
                                  <ComposedChart data={evolucaoMensalCA} margin={{ top: 24, right: 16, left: 8, bottom: 4 }} barGap={10} barCategoryGap="12%">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                                    <YAxis domain={[0, 'auto']} tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                                    <Tooltip
                                      formatter={(value, name) => {
                                        const nomes = { receitas: 'Receitas', despesas: 'Despesas', resultado: 'Resultado Líquido' };
                                        return [formatarValor(value), nomes[name] || name];
                                      }}
                                    />
                                    <Legend />
                                    <Bar dataKey="receitas" name="receitas" fill="#1fa971" radius={[6, 6, 0, 0]} maxBarSize={120}
                                      label={evolucaoMensalCA.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 15, fill: '#1fa971', fontWeight: 700 } : false} />
                                    <Bar dataKey="despesas" name="despesas" fill="#e5484d" radius={[6, 6, 0, 0]} maxBarSize={120}
                                      label={evolucaoMensalCA.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 15, fill: '#e5484d', fontWeight: 700 } : false} />
                                    <Line type="monotone" dataKey="resultado" name="resultado" stroke="#00205b" strokeWidth={3} dot={{ r: 5, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }} />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="text-center py-5 text-muted">
                                  <i className="fas fa-chart-line fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                                  <p>Sem lançamentos dessa categoria no período</p>
                                </div>
                              )}
                            </div>

                            <div className="col-lg-6 mb-3">
                              <h6 className="text-muted mb-3">
                                <i className="fas fa-chart-pie me-2"></i>
                                {catSelCA ? `${limparCategoria(catSelCA.categoria)} vs. demais` : 'Composição das Despesas'}
                              </h6>
                              {(() => {
                                if (donutViewCA) {
                                  return donutViewCA.some((d) => d.valor > 0) ? (
                                    <DonutDespesas dados={donutViewCA} total={totalTipoSelCA} cores={CORES_PIZZA} rotulo={catSelCA._tipo === 'receita' ? 'Receitas' : 'Despesas'} />
                                  ) : (
                                    <div className="text-center py-5 text-muted">
                                      <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                                      <p>Sem valor dessa categoria no período</p>
                                    </div>
                                  );
                                }
                                const base = (dadosDRE.despesas || [])
                                  .filter((d) => !String(d.categoria || '').toLowerCase().includes('transfer'))
                                  .map((d) => ({ nome: limparCategoria(d.categoria), valor: Math.abs(d.total || 0) }))
                                  .filter((d) => d.valor > 0)
                                  .sort((a, b) => b.valor - a.valor);
                                const top = base.slice(0, 6);
                                const outros = base.slice(6).reduce((s, it) => s + it.valor, 0);
                                if (outros > 0) top.push({ nome: 'Outros', valor: outros });
                                const total = top.reduce((s, d) => s + d.valor, 0);
                                return top.length > 0 ? (
                                  <DonutDespesas dados={top} total={total} cores={CORES_PIZZA} rotulo="Despesas" />
                                ) : (
                                  <div className="text-center py-5 text-muted">
                                    <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                                    <p>Sem despesas no período</p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        <div className="alert border-start border-4 mb-0 fmp-insight fmp-ia-insight">
  <div className="row align-items-center">
    <div className="col-md-8">
      <h6 className="alert-heading">
        <i className="fas fa-lightbulb me-2 text-warning"></i>
        Análise Inteligente - Despesas e Receitas
      </h6>
      <div style={{ fontSize: '15px', lineHeight: 1.65, color: '#334c73' }}>
        <strong style={{ color: '#00205b' }}>Performance Financeira:</strong>{' '}
        {dadosDRE.resumo_dre.margem_liquida > 20 
          ? `A empresa apresenta uma excelente eficiência operacional, com margem líquida de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%. Esse resultado indica forte controle de custos e alta capacidade de geração de lucro. O cenário é favorável para expansão, reinvestimentos ou fortalecimento do caixa.`
          
          : dadosDRE.resumo_dre.margem_liquida > 10 
          ? `A margem líquida de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}% demonstra uma operação saudável e sustentável. Ainda assim, existem oportunidades para ganho de eficiência, especialmente na otimização de custos e aumento da rentabilidade.`
          
          : dadosDRE.resumo_dre.margem_liquida > 0 
          ? `A empresa opera com margem líquida positiva de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%, porém em um nível mais apertado. Isso indica que, embora haja lucro, pequenas variações de custos ou receitas podem impactar significativamente o resultado. Recomenda-se atenção na gestão de despesas e precificação.`
          
          : `A margem líquida negativa de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}% indica que a operação está gerando prejuízo no período analisado. Esse cenário exige ação imediata, com revisão detalhada de custos, despesas e estrutura de receitas para reequilibrar o resultado financeiro.`
        }
        {' '}Para uma análise mais detalhada, clique nas barras do gráfico e explore os lançamentos que compõem o resultado.
      </div>
    </div>

    <div className="col-md-4 text-end">
      <span className="badge mb-1 d-block" style={{backgroundColor: '#00205b', color: '#fff'}}>
        <i className="fas fa-calculator me-1"></i>
        {dadosDRE.resumo_dre.periodo_dias} dias analisados
      </span>
      <span className="badge" style={{backgroundColor: '#00b5cc', color: '#00205b'}}>
        <i className="fas fa-database me-1"></i>
        {dadosDRE.resumo_dre.total_movimentos} movimentos
      </span>
    </div>
  </div>
</div>
         
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-calculator fa-3x mb-3"></i>
                        <p>Selecione o período e clique em "Filtrar" para visualizar o DRE</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Impostos pagos por tipo */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-file-invoice-dollar me-2 text-primary"></i>
                      Impostos pagos por tipo
                      <span className="badge ms-2" style={{ backgroundColor: '#eef4ff', color: '#00205b', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #d4e2fb' }}>
                        <i className="fas fa-hand-pointer me-1"></i>Clique nas barras
                      </span>
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {[3, 6, 12].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            const [ini, fim] = rangeUltimosMeses(n);
                            setImpostosIni(ini); setImpostosFim(fim); setImpostosMeses(n);
                            carregarImpostos(ini, fim);
                          }}
                          style={{
                            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600,
                            border: '1px solid ' + (impostosMeses === n ? '#00205b' : '#d4e2fb'),
                            backgroundColor: impostosMeses === n ? '#00205b' : '#fff',
                            color: impostosMeses === n ? '#fff' : '#00205b',
                          }}
                        >
                          {n} meses
                        </button>
                      ))}
                      <span className="text-muted small ms-1">De</span>
                      <input type="month" className="form-control form-control-sm" style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={impostosIni} max={impostosFim || undefined}
                        onChange={(e) => { const ini = e.target.value; setImpostosIni(ini); setImpostosMeses(0); if (ini && impostosFim) carregarImpostos(ini, impostosFim); }} />
                      <span className="text-muted small">até</span>
                      <input type="month" className="form-control form-control-sm" style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={impostosFim} min={impostosIni || undefined}
                        onChange={(e) => { const fim = e.target.value; setImpostosFim(fim); setImpostosMeses(0); if (impostosIni && fim) carregarImpostos(impostosIni, fim); }} />
                    </div>
                  </div>
                  <div className="card-body">
                    {carregandoImpostos ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#00205b' }}></div>
                        <p className="mt-3 text-muted">Carregando impostos...</p>
                      </div>
                    ) : impostosChart.ok ? (
                      <>
                        <div className="row mb-4">
                          <div className="col-md-6 text-center border-end">
                            <h6 className="text-muted small mb-1">Total de impostos no período</h6>
                            <h4 className="mb-0" style={{ color: '#e5484d' }}>{formatarValor(impostosChart.total)}</h4>
                          </div>
                          <div className="col-md-6 text-center">
                            <h6 className="text-muted small mb-1">Tipos de imposto</h6>
                            <h4 className="mb-0" style={{ color: '#00205b' }}>{impostosChart.tipos.length}</h4>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-7 mb-3">
                            <h6 className="text-muted small mb-2" style={{ fontWeight: 600 }}>Impostos por tipo</h6>
                            <GraficoImpostos linhas={impostosChart.linhas} tipos={impostosChart.tipos} onSegmentClick={abrirLancImpostos} />
                          </div>
                          <div className="col-lg-5 mb-3">
                            <h6 className="text-muted small mb-2" style={{ fontWeight: 600 }}>Evolução do total pago</h6>
                            <EvolucaoImpostos linhas={impostosChart.linhas} tipos={impostosChart.tipos} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-file-invoice-dollar fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>Não foram encontrados impostos no período selecionado.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Faturado (competência) x Recebido */}
            {fatRec.ok && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-file-invoice me-2" style={{ color: '#00205b' }}></i>
                        Faturado (competência) x Recebido
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row mb-4">
                        <div className="col-md-3 text-center border-end">
                          <h6 className="text-muted small mb-1">Faturado</h6>
                          <h4 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(fatRec.totalFat)}</h4>
                        </div>
                        <div className="col-md-3 text-center border-end">
                          <h6 className="text-muted small mb-1">Recebido</h6>
                          <h4 className="mb-0" style={{ color: '#1fa971', cursor: 'pointer' }} title="Ver lançamentos recebidos do período" onClick={() => abrirLancFatRecPeriodo('recebido')}>
                            {formatarValor(fatRec.totalRec)}
                            <i className="fas fa-up-right-from-square ms-2" style={{ fontSize: 11, opacity: 0.55 }}></i>
                          </h4>
                        </div>
                        <div className="col-md-3 text-center border-end">
                          <h6 className="text-muted small mb-1">Em atraso</h6>
                          <h4 className="mb-0" style={{ color: '#e5484d', cursor: 'pointer' }} title="Ver lançamentos em atraso do período" onClick={() => abrirLancFatRecPeriodo('atrasado')}>
                            {formatarValor(fatRec.totalAtraso || 0)}
                            <i className="fas fa-up-right-from-square ms-2" style={{ fontSize: 11, opacity: 0.55 }}></i>
                          </h4>
                        </div>
                        <div className="col-md-3 text-center">
                          <h6 className="text-muted small mb-1">% recebido</h6>
                          <h4 className="mb-0" style={{ color: '#b5891f' }}>
                            {(fatRec.totalFat > 0 ? (fatRec.totalRec / fatRec.totalFat) * 100 : 0).toFixed(1)}%
                          </h4>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-7 mb-3">
                          <ResponsiveContainer width="100%" height={340}>
                            <BarChart
                              data={fatRec.linhas.map((l) => {
                                const f = l.faturado || 0; const r = l.recebido || 0; const alvo = Math.max(f, r);
                                return { ...l, gapFat: Math.max(0, alvo - f), gapRec: Math.max(0, alvo - r) };
                              })}
                              margin={{ top: 20, right: 16, left: 8, bottom: 4 }}
                              barGap={8}
                              barCategoryGap="24%"
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                              <YAxis tickFormatter={(v) => formatarValor(v)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                              <Tooltip
                                cursor={{ fill: 'rgba(0,32,91,0.06)' }}
                                content={({ active, payload, label }) => {
                                  if (!active || !payload || !payload.length) return null;
                                  const row = payload[0].payload || {};
                                  const fat = row.faturado || 0; const rec = row.recebido || 0; const av = Math.max(fat - rec, 0);
                                  const linha = (cor, nome, val) => (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: cor, display: 'inline-block' }}></span>{nome}
                                      </span>
                                      <b style={{ color: '#fff', fontSize: 13 }}>{formatarValor(val)}</b>
                                    </div>
                                  );
                                  return (
                                    <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 230, border: '1px solid rgba(146,162,196,0.18)' }}>
                                      <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
                                      {linha('#00205b', 'Faturado', fat)}
                                      {linha('#1fa971', 'Recebido', rec)}
                                      <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8 }}>{linha('#aab2c0', 'A receber', av)}</div>
                                    </div>
                                  );
                                }}
                              />
                              <Legend formatter={(key) => (key === 'faturado' ? 'Faturado' : 'Recebido')} payload={[{ value: 'faturado', type: 'square', color: '#00205b' }, { value: 'recebido', type: 'square', color: '#1fa971' }]} />
                              <Bar dataKey="faturado" stackId="fat" name="faturado" fill="#00205b" radius={[6, 6, 0, 0]} maxBarSize={70} />
                              <Bar dataKey="gapFat" stackId="fat" fill="#e6eaf0" radius={[6, 6, 0, 0]} maxBarSize={70} legendType="none" isAnimationActive={false} />
                              <Bar dataKey="recebido" stackId="rec" name="recebido" fill="#1fa971" radius={[6, 6, 0, 0]} maxBarSize={70} />
                              <Bar dataKey="gapRec" stackId="rec" fill="#e6eaf0" radius={[6, 6, 0, 0]} maxBarSize={70} legendType="none" isAnimationActive={false} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="col-lg-5 mb-3">
                          <DonutDespesas
                            dados={[
                              { nome: 'Recebido', valor: fatRec.totalRec },
                              { nome: 'A vencer', valor: Math.max(fatRec.totalFat - fatRec.totalRec - (fatRec.totalAtraso || 0), 0) },
                              { nome: 'Em atraso', valor: fatRec.totalAtraso || 0 },
                            ]}
                            total={fatRec.totalFat}
                            cores={['#1fa971', '#f1be48', '#e5484d']}
                            rotulo="Faturado"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                ANÁLISE DE INVESTIMENTOS
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-chart-line me-2"></i>
                      Análise de Investimentos
                    </h5>
                  </div>

                  <div className="card-body">
                    {carregandoInvestimentos ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{color: '#00205b'}}></div>
                        <p className="mt-3 text-muted">Carregando Investimentos...</p>
                      </div>
                    ) : dadosInvestimentos && dadosInvestimentos.contas_investimento && dadosInvestimentos.contas_investimento.length > 0 ? (
                      <>
                        {/* Resumo Investimentos */}
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Total Investido</h6>
                            <h5 className="text-primary mb-0">
                              <i className="fas fa-coins me-1"></i>
                              {formatarValor(dadosInvestimentos.resumo?.total_saldo_investimentos || 0)}
                            </h5>
                            <small className="text-muted">{dadosInvestimentos.resumo?.total_contas || 0} contas</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Rendimento Líquido</h6>
                            <h5 className="text-success mb-0">
                              <i className="fas fa-arrow-trend-up me-1"></i>
                              {formatarValor(dadosInvestimentos.contas_investimento.reduce((acc, c) => acc + (c.rendimento_liquido || 0), 0))}
                            </h5>
                            <small className="text-success">+{(dadosInvestimentos.resumo?.rentabilidade_geral || 0).toFixed(2)}%</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Maior Rendimento</h6>
                            {(() => {
                              const melhor = dadosInvestimentos.contas_investimento.reduce((max, c) =>
                                (c.rentabilidade_percentual || 0) > (max?.rentabilidade_percentual || -Infinity) ? c : max, null);
                              return (
                                <>
                                  <h5 className="text-success mb-0" style={{fontSize: '14px'}}>{melhor?.nome_curto || melhor?.nome_conta || 'N/A'}</h5>
                                  <small className="text-success">+{(melhor?.rentabilidade_percentual || 0).toFixed(2)}%</small>
                                </>
                              );
                            })()}
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">Rentabilidade Geral</h6>
                            <h5 className="text-info mb-0">
                              <i className="fas fa-percentage me-1"></i>
                              {(dadosInvestimentos.resumo?.rentabilidade_geral || 0).toFixed(2)}%
                            </h5>
                            <small className="text-muted">Últimos 6 meses</small>
                          </div>
                        </div>

                        {/* Cards Investimentos */}
                        <div className="row mb-4">
                          {dadosInvestimentos.contas_investimento.map((conta, index) => (
                            <div key={index} className="col-lg-4 col-md-6 mb-3">
                              <div
                                className="card border-0 shadow-sm h-100"
                                style={{ cursor: 'pointer' }}
                                onClick={() => mostrarDetalhesInvestimento(conta)}
                              >
                                <div className="card-header" style={{backgroundColor: (conta.cor || '#00205b') + '15', borderLeft: `4px solid ${conta.cor || '#00205b'}`}}>
                                  <h6 className="mb-0 d-flex align-items-center">
                                    <i className={`${conta.icone || 'fas fa-chart-line'} me-2`} style={{color: conta.cor || '#00205b'}}></i>
                                    <div className="flex-grow-1">
                                      <div style={{fontSize: '13px', fontWeight: '600'}}>
                                        {conta.nome_curto || conta.nome_conta || 'Investimento'}
                                      </div>
                                    </div>
                                  </h6>
                                </div>
                                <div className="card-body">
                                  <div className="text-center mb-3">
                                    <small className="text-muted d-block mb-1">Saldo Disponível</small>
                                    <h5 className="text-primary mb-0">
                                      {formatarValor(conta.saldo_atual || 0)}
                                    </h5>
                                  </div>
                                  <div className="row text-center">
                                    <div className="col-6 border-end">
                                      <small className="text-muted d-block">Rendimento</small>
                                      <div className="text-success">
                                        {formatarValor(conta.rendimento_liquido || 0)}
                                      </div>
                                      <small className="text-success">+{(conta.rentabilidade_percentual || 0).toFixed(2)}%</small>
                                    </div>
                                    <div className="col-6">
                                      <small className="text-muted d-block">% Total Inv.</small>
                                      <div className="text-info">
                                        {dadosInvestimentos.resumo?.total_saldo_investimentos > 0
                                          ? ((conta.saldo_atual / dadosInvestimentos.resumo.total_saldo_investimentos) * 100).toFixed(1)
                                          : '0.0'}%
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Insight Investimentos */}
                        <div className="alert border-start border-4 mb-0 mt-3 fmp-insight fmp-ia-insight">
                          <div className="row align-items-center">
                            <div className="col-md-9">
                              <h6 className="alert-heading">
                                <i className="fas fa-lightbulb me-2 text-warning"></i>
                                Análise Inteligente - Investimentos
                              </h6>
                              <div style={{ fontSize: '15px', lineHeight: 1.65, color: '#334c73' }}>
                                <strong style={{ color: '#00205b' }}>Performance dos Investimentos:</strong>{' '}
                                A carteira apresenta uma rentabilidade de {(dadosInvestimentos.resumo?.rentabilidade_geral || 0).toFixed(2)}%,
                                com rendimento líquido de {formatarValor(dadosInvestimentos.contas_investimento.reduce((acc, c) => acc + (c.rendimento_liquido || 0), 0))} no período analisado.
                                {(() => {
                                  const melhor = dadosInvestimentos.contas_investimento.reduce((max, c) =>
                                    (c.rentabilidade_percentual || 0) > (max?.rentabilidade_percentual || -Infinity) ? c : max, null);
                                  return melhor ? (
                                    <> O principal destaque é <strong style={{ color: '#00205b' }}>{melhor.nome_curto || melhor.nome_conta}</strong>, com rentabilidade de {(melhor.rentabilidade_percentual || 0).toFixed(2)}%.</>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                            <div className="col-md-3 text-end">
                              <span className="badge" style={{backgroundColor: '#00205b', color: '#fff'}}>
                                <i className="fas fa-chart-line me-1"></i>
                                {dadosInvestimentos.resumo?.total_contas || 0} Contas Monitoradas
                              </span>
                            </div>
                          </div>
                        </div>

                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-piggy-bank fa-3x mb-3" style={{color: '#00205b'}}></i>
                        <p>Nenhuma conta de investimento configurada ou sem movimentações nos últimos 6 meses</p>
                        <small>Configure contas com categoria "investimento" em Configurações</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>



{/* ========================================
          MODAL - DETALHES DO DIA (FLUXO DIÁRIO)
      ======================================== */}
      {modalFluxoDiario.aberto && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
                <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                  <i className="fas fa-calendar-day me-2" style={{color: '#212529'}}></i>
                  Detalhes - Dia {modalFluxoDiario.dados.dia} ({modalFluxoDiario.dados.dia_semana})
                </h5>
                <button type="button" className="btn-close" onClick={fecharModalFluxoDiario}></button>
              </div>
              
              <div className="modal-body">
                {/* Resumo do Dia */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <div className="row text-center">
                          <div className="col-md-3 border-end">
                            <small className="text-muted d-block">Data</small>
                            <h6 className="text-dark mb-0">
                              <i className="fas fa-calendar me-1"></i>
                              {modalFluxoDiario.dados.data_completa}
                            </h6>
                          </div>
                          <div className="col-md-3 border-end">
                            <small className="text-muted d-block">Entradas</small>
                            <h6 className="text-success mb-0">
                              {formatarValor(modalFluxoDiario.dados.entradas)}
                            </h6>
                          </div>
                          <div className="col-md-3 border-end">
                            <small className="text-muted d-block">Saídas</small>
                            <h6 className="text-danger mb-0">
                              {formatarValor(Math.abs(modalFluxoDiario.dados.saidas))}
                            </h6>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Saldo do Dia</small>
                            <h6 className={`mb-0 ${(modalFluxoDiario.dados.entradas + modalFluxoDiario.dados.saidas) >= 0 ? 'text-success' : 'text-danger'}`}>
                              {formatarValor(modalFluxoDiario.dados.entradas + modalFluxoDiario.dados.saidas)}
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Movimentos do Dia */}
                {modalFluxoDiario.dados.movimentos && modalFluxoDiario.dados.movimentos.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{width: '8%'}}>
                            <i className="fas fa-circle me-1"></i>
                            Tipo
                          </th>
                          <th style={{width: '35%'}}>
                            <i className="fas fa-file-alt me-1"></i>
                            Descrição
                          </th>
                          <th style={{width: '15%'}}>
                            <i className="fas fa-tag me-1"></i>
                            Categoria
                          </th>
                          <th style={{width: '12%'}}>
                            <i className="fas fa-university me-1"></i>
                            Conta
                          </th>
                          <th style={{width: '12%'}} className="text-end">
                            <i className="fas fa-dollar-sign me-1"></i>
                            Valor
                          </th>
                          <th style={{width: '10%'}} className="text-center">
                            <i className="fas fa-info-circle me-1"></i>
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalFluxoDiario.dados.movimentos.map((mov, idx) => (
                          <tr key={idx}>
                            <td>
                              {mov.tipo === 'TRANSFERENCIA' ? (
                                <span className="badge bg-info">
                                  <i className="fas fa-exchange-alt me-1"></i>
                                  Transfer
                                </span>
                              ) : mov.total >= 0 ? (
                                <span className="badge bg-success">
                                  <i className="fas fa-arrow-down me-1"></i>
                                  Entrada
                                </span>
                              ) : (
                                <span className="badge bg-danger">
                                  <i className="fas fa-arrow-up me-1"></i>
                                  Saída
                                </span>
                              )}
                            </td>
                            <td>
                              <small>{mov.descricao || 'Sem descrição'}</small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {mov.nome_categoria || 'Sem categoria'}
                              </small>
                            </td>
                            <td>
                              <small className="text-primary">
                                {mov.nome_conta || 'N/A'}
                              </small>
                            </td>
                            <td className="text-end">
                              <strong className={mov.total >= 0 ? 'text-success' : 'text-danger'}>
                                {formatarValor(mov.total)}
                              </strong>
                            </td>
                            <td className="text-center">
                              {['RECEBIDO', 'PAGO', 'LIQUIDADO'].includes(mov.status) ? (
                                <span className="badge bg-success">
                                  <i className="fas fa-check me-1"></i>
                                  Realizado
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  <i className="fas fa-clock me-1"></i>
                                  Previsto
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>Nenhum movimento registrado neste dia</p>
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={fecharModalFluxoDiario}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================
          MODAL - DETALHES DO INVESTIMENTO
      ======================================== */}
      {modalInvestimento.aberto && modalInvestimento.dados && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
                <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                  <i className={`fas ${modalInvestimento.dados.icone || 'fa-piggy-bank'} me-2`} style={{color: '#212529'}}></i>
                  {modalInvestimento.dados.nome_conta}
                </h5>
                <button type="button" className="btn-close" onClick={fecharModalInvestimento}></button>
              </div>
              
              <div className="modal-body">
                {/* Cards Resumo */}
                <div className="row g-3 mb-4">
                  {[
                    { rot: 'Saldo Atual', val: formatarValor(modalInvestimento.dados.saldo_atual), cor: '#00205b', sub: modalInvestimento.dados.nome_curto || '' },
                    { rot: 'Total Aportes', val: formatarValor(modalInvestimento.dados.total_entradas), cor: '#00b5cc', sub: `${modalInvestimento.dados.qtd_entradas || 0} mov.` },
                    { rot: 'Total Resgates', val: formatarValor(modalInvestimento.dados.total_saidas || 0), cor: '#f1be48', sub: `${modalInvestimento.dados.qtd_saidas || 0} mov.` },
                    { rot: 'Rendimentos', val: formatarValor(modalInvestimento.dados.total_rendimentos), cor: '#1fa971', sub: `${modalInvestimento.dados.qtd_rendimentos || 0} mov.` },
                    { rot: 'Rentabilidade', val: `${modalInvestimento.dados.rentabilidade_percentual >= 0 ? '+' : ''}${modalInvestimento.dados.rentabilidade_percentual.toFixed(2)}%`, cor: modalInvestimento.dados.rentabilidade_percentual >= 0 ? '#1fa971' : '#e5484d', sub: 'no período' },
                  ].map((k, i) => (
                    <div className="col-md col-6" key={i}>
                      <div style={{ background: '#fff', border: '1px solid #eef1f6', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
                        <div style={{ fontSize: 11.5, color: '#7d8aa3', marginBottom: 4 }}>{k.rot}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: k.cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{k.val}</div>
                        {k.sub && <div style={{ fontSize: 11, color: '#98a4b8', marginTop: 3 }}>{k.sub}</div>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Gráfico Evolução Mensal - dados da conta selecionada */}
                {(() => {
                  const graficoData = computarGraficoMensalConta(modalInvestimento.dados.movimentos);
                  if (graficoData.length === 0) return null;
                  return (
                  <>
                    <h6 className="mb-3">
                      <i className="fas fa-chart-bar me-2"></i>
                      Evolução Mensal - Últimos 12 Meses
                      <span className="badge ms-2" style={{backgroundColor: '#eaf2ff', color: '#00205b', fontSize: '11px'}}>
                        {modalInvestimento.dados.nome_curto || modalInvestimento.dados.nome_conta}
                      </span>
                    </h6>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={graficoData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                        <YAxis
                          yAxisId="left"
                          tickFormatter={(value) => `${(value/1000).toFixed(0)}k`}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tickFormatter={(value) => `${(value/1000).toFixed(0)}k`}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(value, name) => {
                            const nomes = { aportes: 'Aportes', resgates: 'Resgates', rendimentos: 'Rendimentos', saldo_acumulado: 'Saldo Total' };
                            return [formatarValor(Math.abs(value)), nomes[name] || name];
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="aportes" name="aportes" fill="#00b5cc" radius={[4, 4, 0, 0]} maxBarSize={46} />
                        <Bar yAxisId="left" dataKey="resgates" name="resgates" fill="#f1be48" radius={[4, 4, 0, 0]} maxBarSize={46} />
                        <Bar yAxisId="left" dataKey="rendimentos" name="rendimentos" fill="#1fa971" radius={[4, 4, 0, 0]} maxBarSize={46} />
                        <Line yAxisId="right" type="monotone" dataKey="saldo_acumulado" name="saldo_acumulado" stroke="#00205b" strokeWidth={2.5} dot={{ fill: '#fff', stroke: '#00205b', strokeWidth: 2, r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                    <hr />
                  </>
                  );
                })()}

                {/* Tabela de Movimentos */}
                {modalInvestimento.dados.movimentos && modalInvestimento.dados.movimentos.length > 0 ? (
                  <>
                    <h6 className="mb-3">
                      <i className="fas fa-list me-2"></i>
                      Histórico de Movimentos (Últimos 12 Meses)
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{width: '12%'}}>Data</th>
                            <th style={{width: '15%'}}>Tipo</th>
                            <th style={{width: '35%'}}>Descrição</th>
                            <th style={{width: '15%'}}>Categoria</th>
                            <th style={{width: '13%'}} className="text-end">Valor</th>
                            <th style={{width: '10%'}} className="text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalInvestimento.dados.movimentos
                            .sort((a, b) => new Date(b.data_vencimento) - new Date(a.data_vencimento))
                            .map((mov, idx) => (
                            <tr key={idx}>
                              <td>
                                <small>{new Date(mov.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}</small>
                              </td>
                              <td>
                                {mov.tipo_movimento === 'Aportes' && (
                                  <span className="badge bg-success">
                                    <i className="fas fa-arrow-down me-1"></i>
                                    Aporte
                                  </span>
                                )}
                                {mov.tipo_movimento === 'Resgates' && (
                                  <span className="badge bg-danger">
                                    <i className="fas fa-arrow-up me-1"></i>
                                    Resgate
                                  </span>
                                )}
                                {mov.tipo_movimento === 'Rendimentos' && (
                                  <span className="badge bg-info">
                                    <i className="fas fa-chart-line me-1"></i>
                                    Rendimento
                                  </span>
                                )}
                                {mov.tipo_movimento === 'Impostos' && (
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-receipt me-1"></i>
                                    Imposto
                                  </span>
                                )}
                              </td>
                              <td>
                                <small>{mov.descricao || 'Sem descrição'}</small>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {mov.nome_categoria || 'Sem categoria'}
                                </small>
                              </td>
                              <td className="text-end">
                                <strong className={['Resgates', 'Impostos'].includes(mov.tipo_movimento) ? 'text-danger' : 'text-success'}>
                                  {formatarValor(Math.abs(mov.total))}
                                </strong>
                              </td>
                              <td className="text-center">
                                {['RECEBIDO', 'PAGO', 'LIQUIDADO'].includes(mov.status) ? (
                                  <span className="badge bg-success">
                                    <i className="fas fa-check"></i>
                                  </span>
                                ) : (
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-clock"></i>
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-2x mb-2"></i>
                    <p className="mb-0">Nenhum movimento registrado</p>
                  </div>
                )}

                {/* Insights */}
                <div className="alert border-start border-4 mt-4 mb-0 fmp-insight fmp-ia-insight">
                  <h6 className="alert-heading">
                    <i className="fas fa-lightbulb me-2 text-warning"></i>
                    Análise do Investimento
                  </h6>
                  <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#334c73' }}>
                    <ul className="mb-0">
                      <li>
                        <strong>Performance:</strong> Rentabilidade de{' '}
                        <span className={modalInvestimento.dados.rentabilidade_percentual >= 0 ? 'text-success' : 'text-danger'}>
                          {modalInvestimento.dados.rentabilidade_percentual >= 0 ? '+' : ''}
                          {modalInvestimento.dados.rentabilidade_percentual.toFixed(2)}%
                        </span>
                        {' '}nos últimos 6 meses
                      </li>
                      <li>
                        <strong>Balanço:</strong> Aportes de {formatarValor(modalInvestimento.dados.total_entradas)}
                        {modalInvestimento.dados.total_saidas > 0 && (
                          <> - Resgates de {formatarValor(modalInvestimento.dados.total_saidas)}</>
                        )}
                      </li>
                      <li>
                        <strong>Ganhos:</strong> Rendimentos de {formatarValor(modalInvestimento.dados.total_rendimentos)}
                        {modalInvestimento.dados.total_impostos < 0 && (
                          <> - Impostos de {formatarValor(Math.abs(modalInvestimento.dados.total_impostos))}</>
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={fecharModalInvestimento}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Relatório Completo (todas as categorias, do maior ao menor) */}
      {modalRelatorio && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
          onClick={() => setModalRelatorio(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-list me-2" style={{ color: '#212529' }}></i>
                  {relatorioDados.titulo}
                </h5>
                <button type="button" className="btn-close" onClick={() => setModalRelatorio(false)}></button>
              </div>

              <div className="modal-header bg-light">
                <div className="w-100 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <i className="fas fa-sort-amount-down-alt me-1"></i>
                    {relatorioDados.itens.length} categorias · do maior ao menor
                  </small>
                  <strong
                    className={relatorioDados.tipo === 'despesas' ? 'text-danger' : 'text-success'}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatarValor(relatorioDados.itens.reduce((s, it) => s + Math.abs(it.total || 0), 0))}
                  </strong>
                </div>
              </div>

              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {relatorioDados.itens.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm align-middle">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: '7%' }}>#</th>
                          <th>Categoria</th>
                          <th className="text-center" style={{ width: '14%' }}>Movs.</th>
                          <th className="text-end" style={{ width: '24%' }}>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatorioDados.itens.map((it, i) => (
                          <tr
                            key={i}
                            style={{ cursor: 'pointer' }}
                            title="Clique para ver os lançamentos"
                            onClick={() => {
                              const detalhes = it.detalhes || [];
                              abrirModalCategoriaDRE(
                                { categoria: it.categoria, total: it.total, quantidade: detalhes.length, movimentos: detalhes },
                                relatorioDados.tipo === 'despesas' ? 'despesa' : 'receita'
                              );
                            }}
                          >
                            <td className="text-muted">{i + 1}</td>
                            <td><strong>{limparCategoria(it.categoria)}</strong></td>
                            <td className="text-center">
                              <span className="badge bg-light text-dark">{(it.detalhes || []).length}</span>
                            </td>
                            <td className="text-end">
                              <strong
                                className={relatorioDados.tipo === 'despesas' ? 'text-danger' : 'text-success'}
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                              >
                                {formatarValor(Math.abs(it.total || 0))}
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                    <p>Nenhuma categoria encontrada</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalRelatorio(false)}>
                  <i className="fas fa-times me-1"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================
          MODAL - DETALHES DA CATEGORIA DRE
      ======================================== */}
    {/* Modal Detalhes Categoria DRE */}
{modalCategoria && modalCategoria.dados && (
  <div 
    className="modal fade show d-block" 
    style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070}}
    onClick={() => setModalCategoria(false)}
  >
    <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
      <div className="modal-content">
        <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
          <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
            <i className="fas fa-list me-2" style={{color: '#212529'}}></i>
            Detalhes: {modalCategoria.dados.categoria}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setModalCategoria(false)}
          ></button>
        </div>
        
        <div className="modal-header bg-light">
          <div className="w-100 text-center">
            <div className="row">
              <div className="col-md-4">
                <small className="text-muted d-block">Total</small>
                <h5 className={`mb-0 ${modalCategoria.tipo === 'receita' ? 'text-success' : 'text-danger'}`}>
                  {formatarValor(Math.abs(modalCategoria.dados.total))}
                </h5>
              </div>
              <div className="col-md-4">
                <small className="text-muted d-block">Quantidade</small>
                <h5 className="text-primary mb-0">
                  {modalCategoria.dados.quantidade} movimentos
                </h5>
              </div>
              <div className="col-md-4">
                <small className="text-muted d-block">Tipo</small>
                <span className={`badge ${modalCategoria.tipo === 'receita' ? 'bg-success' : 'bg-danger'}`}>
                  {modalCategoria.tipo === 'receita' ? 'RECEITA' : 'DESPESA'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
          {modalCategoria.dados.movimentos && modalCategoria.dados.movimentos.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead className="table-light sticky-top">
                  <tr>
                    <th width="15%">Data</th>
                    <th width="55%">Descrição</th>
                    <th width="15%" className="text-end">Valor</th>
                    <th width="15%" className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {modalCategoria.dados.movimentos.map((movimento, index) => (
                    <tr key={index}>
                      <td>
                        <small className="text-muted">
                          {movimento.data_vencimento || movimento.data}
                        </small>
                      </td>
                      <td>
                        <small>{movimento.descricao || movimento.historico}</small>
                        {movimento.fornecedor && (
                          <div>
                            <small className="text-muted fst-italic">
                              <i className="fas fa-building me-1"></i>
                              {movimento.fornecedor}
                            </small>
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <strong className={modalCategoria.tipo === 'receita' ? 'text-success' : 'text-danger'}>
                          {formatarValor(Math.abs(movimento.valor ?? movimento.total ?? 0))}
                        </strong>
                      </td>
                      <td className="text-center">
                        <span className="badge badge-sm bg-success">
                          <i className="fas fa-check me-1"></i>
                          Realizado
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3" style={{color: '#ff6b35'}}></i>
              <p>Nenhum movimento encontrado para esta categoria</p>
              <small>Verifique se o backend está retornando os dados corretamente</small>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => setModalCategoria(false)}
          >
            <i className="fas fa-times me-1"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Modal de lançamentos de impostos */}
      {impostosLanc.aberto && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={() => setImpostosLanc((p) => ({ ...p, aberto: false }))}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-file-invoice-dollar me-2" style={{ color: '#e5484d' }}></i>
                  {impostosLanc.titulo}
                </h5>
                <button type="button" className="btn-close" onClick={() => setImpostosLanc((p) => ({ ...p, aberto: false }))}></button>
              </div>
              <div className="modal-body">
                {impostosLanc.carregando ? (
                  <div className="text-center py-5"><div className="spinner-border" style={{ color: '#00205b' }}></div></div>
                ) : impostosLanc.itens.length > 0 ? (
                  (() => {
                    const temVenc = impostosLanc.itens.some((l) => l.vencimento);
                    const temStatus = impostosLanc.itens.some((l) => l.status);
                    const temCategoria = impostosLanc.itens.some((l) => l.categoria);
                    const fmtD = (d) => (d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : '—');
                    const corStatus = (s) => { const u = String(s || '').toUpperCase(); if (u.includes('RECEB')) return '#1fa971'; if (u.includes('ATRAS')) return '#e5484d'; return '#7d8aa3'; };
                    const colTotal = 2 + (temVenc ? 1 : 0) + (temStatus ? 1 : 0) + (temCategoria ? 1 : 0);
                    const thBorda = { borderBottom: '2px solid #eef1f6' };
                    return (
                      <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead>
                            <tr style={{ color: '#7d8aa3', fontSize: 12 }}>
                              <th style={thBorda}>Competência</th>
                              {temCategoria && <th style={thBorda}>Categoria</th>}
                              <th style={thBorda}>Descrição</th>
                              {temVenc && <th style={thBorda}>Vencimento</th>}
                              {temStatus && <th className="text-center" style={thBorda}>Status</th>}
                              <th className="text-end" style={thBorda}>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {impostosLanc.itens.map((l, i) => (
                              <tr key={i}>
                                <td style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#475467' }}>{fmtD(l.data)}</td>
                                {temCategoria && <td style={{ fontSize: 13, color: '#475467', fontWeight: 600 }}>{l.categoria || '—'}</td>}
                                <td style={{ fontSize: 13, color: '#475467' }}>{l.nome}</td>
                                {temVenc && <td style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#475467' }}>{fmtD(l.vencimento)}</td>}
                                {temStatus && (
                                  <td className="text-center">
                                    <span className="badge" style={{ background: corStatus(l.status) + '22', color: corStatus(l.status), fontWeight: 600, fontSize: 11 }}>{l.status || '—'}</span>
                                  </td>
                                )}
                                <td className="text-end" style={{ fontSize: 13, fontWeight: 600, color: '#0e1726', whiteSpace: 'nowrap' }}>{formatarValor(l.valor)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th colSpan={colTotal} className="text-end" style={{ color: '#7d8aa3' }}>Total ({impostosLanc.itens.length})</th>
                              <th className="text-end" style={{ color: '#00205b' }}>{formatarValor(impostosLanc.total)}</th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-5 text-muted"><i className="fas fa-inbox fa-2x mb-2"></i><p className="mb-0">Nenhum lançamento encontrado</p></div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setImpostosLanc((p) => ({ ...p, aberto: false }))}>
                  <i className="fas fa-times me-1"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================
          FOOTER
      ======================================== */}
  {/* ========================================
            FOOTER - COPYRIGHT
        ======================================== */}
        </div>

        <div className="row mt-2 mb-4">
          <div className="col-12">
            <div className="card border-0" style={{ background: 'radial-gradient(120% 140% at 80% -10%, #0a2a6b 0%, #04122c 55%, #030d22 100%)' }}>
              <div className="card-body py-3">
                <div className="text-center">
                  <p className="mb-0" style={{color: '#ffffff', fontSize: '0.9rem'}}>
                    <i className="fas fa-shield-alt me-2"></i>
                    <strong>Financial Management Platform - FMP</strong>
                    <span className="mx-2">|</span>
                    Todos os direitos reservados © 2025
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      <AssistenteFinanceiro clienteId={cliente_id} />
    </div>
  );
}

export default DashboardContaAzul;      